import React, { useEffect, useRef, useState } from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { api, publicFileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { CheckCircle2, FileSpreadsheet, Image as ImageIcon, Trophy, Shuffle, Dice5, Save, PlusCircle, X, Sparkles, Upload, Trash2, Rocket, Pencil, Send, BellRing, Users2, CalendarDays, Copy, Link2, ShieldOff, Wand2 } from "lucide-react";
import GironeBadge from "@/components/GironeBadge";
import { GufoIcon } from "@/components/GufoIcon";

const GIRONI = ["A", "B", "C", "D"];

function GoLiveResetCard() {
  const [wipeUsers, setWipeUsers] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [confirmUsersText, setConfirmUsersText] = useState("");
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    if (confirmText !== "CANCELLA TUTTO") {
      toast.error('Digita "CANCELLA TUTTO" per confermare');
      return;
    }
    if (wipeUsers && confirmUsersText !== "CANCELLA UTENTI") {
      toast.error('Per eliminare gli utenti serve anche "CANCELLA UTENTI"');
      return;
    }
    if (!window.confirm(
      wipeUsers
        ? "GO-LIVE RESET · Verranno cancellati TUTTI i dati di gioco E tutti gli utenti non-admin. Gli utenti saranno salvati in users_archive e ripristinabili. Continuare?"
        : "Verranno cancellati TUTTI i dati di gioco. Gli utenti restano. Continuare?"
    )) return;
    setRunning(true); setResult(null);
    try {
      const { data } = await api.post("/admin/dev/wipe-game-data", {
        confirm: "CANCELLA TUTTO",
        wipe_users: wipeUsers,
        confirm_users: wipeUsers ? "CANCELLA UTENTI" : undefined,
      });
      setResult(data);
      toast.success(wipeUsers
        ? `Ambiente azzerato · ${data.users_archived || 0} utenti archiviati (ripristinabili)`
        : "Dati di gioco azzerati");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore wipe");
    } finally {
      setRunning(false); setConfirmText(""); setConfirmUsersText("");
    }
  };

  return (
    <div className="rounded-2xl border-2 border-red-300 bg-red-50 p-6" data-testid="admin-go-live-reset">
      <div className="flex items-start gap-3">
        <Trash2 className="w-6 h-6 text-red-700 shrink-0 mt-1" />
        <div className="flex-1">
          <div className="display font-black text-lg text-red-900">Go-Live Reset · Azzera dati per la produzione</div>
          <p className="text-red-900/80 text-sm mt-1 leading-relaxed">
            One-click reset per andare in <b>Produzione</b> con un ambiente pulito. Cancella
            squadre, calendario, coppa, mercati, scommesse, articoli, gufate, supporter, notifiche
            <b> e anche il pool calciatori</b> (<code>player_ratings</code>): la sezione Calciatori
            ripartirà da zero e si ripopolerà con i roster caricati all'asta.
            <br /><b>Preserva sempre:</b> admin, configurazioni di sistema.
            <br />Con "elimina utenti" attivo, <b>gli utenti vengono prima archiviati</b> in <code>users_archive</code> e sono ripristinabili con il card in basso finché non lanci un nuovo wipe.
          </p>
          <div className="mt-4 flex flex-wrap gap-3 items-center">
            <label className="flex items-center gap-2 text-sm text-red-900 cursor-pointer" data-testid="reset-wipe-users-toggle">
              <input type="checkbox" checked={wipeUsers} onChange={(e) => { setWipeUsers(e.target.checked); setConfirmUsersText(""); }} className="w-4 h-4 accent-red-600" />
              Elimina anche <b>tutti gli utenti non-admin</b> (richiede doppia conferma)
            </label>
          </div>
          <div className="mt-3 flex flex-wrap gap-2 items-center">
            <Input value={confirmText} onChange={(e) => setConfirmText(e.target.value.toUpperCase())}
                   placeholder='Digita "CANCELLA TUTTO"' className="max-w-xs bg-white border-red-300"
                   data-testid="reset-confirm-input" />
            {wipeUsers && (
              <Input value={confirmUsersText} onChange={(e) => setConfirmUsersText(e.target.value.toUpperCase())}
                     placeholder='Digita "CANCELLA UTENTI"' className="max-w-xs bg-white border-red-500"
                     data-testid="reset-confirm-users-input" />
            )}
            <Button onClick={run}
                    disabled={running || confirmText !== "CANCELLA TUTTO" || (wipeUsers && confirmUsersText !== "CANCELLA UTENTI")}
                    className="bg-red-600 hover:bg-red-700 text-white" data-testid="reset-execute-btn">
              {running ? "Azzeramento in corso…" : "🧨 Azzera tutto"}
            </Button>
          </div>
          {result && (
            <div className="mt-4 space-y-2 text-sm text-red-900/90" data-testid="reset-result">
              <div className="display font-bold">
                Cancellati {result.total_docs_deleted} documenti in {result.collections_wiped} collezioni
                {result.users_deleted ? ` · Utenti eliminati: ${result.users_deleted}` : ""}
                {result.users_archived ? ` · Archiviati (ripristinabili): ${result.users_archived}` : ""}
              </div>
              <div className="text-xs">
                Preservati — Utenti: <b>{result.preserved?.users}</b> (admin: {result.preserved?.admin_users})
              </div>
              <details className="text-xs">
                <summary className="cursor-pointer">Dettaglio per collezione</summary>
                <ul className="mt-1 space-y-0.5">
                  {Object.entries(result.deleted_by_collection || {}).map(([k, v]) => (
                    <li key={k}>{k}: -{v}</li>
                  ))}
                </ul>
              </details>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


function RestoreUsersCard() {
  const [info, setInfo] = useState({ total: 0, archived_at: null, statuses: {} });
  const [running, setRunning] = useState(false);
  const [repairing, setRepairing] = useState(false);
  const [result, setResult] = useState(null);

  const load = async () => {
    try { const { data } = await api.get("/admin/dev/users-archive-status"); setInfo(data || {}); } catch {}
  };
  useEffect(() => { load(); }, []);

  const run = async () => {
    if (!window.confirm(`Ripristinare ${info.total} utenti dall'archivio? Gli utenti con email già esistente vengono saltati.`)) return;
    setRunning(true); setResult(null);
    try {
      const { data } = await api.post("/admin/dev/restore-users-from-archive");
      setResult(data);
      toast.success(`Ripristinati ${data.restored} utenti · ${data.skipped} saltati`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore ripristino");
    } finally { setRunning(false); }
  };

  const repairRoles = async () => {
    if (!window.confirm("Aggiornare i ruoli in player_ratings da P/D/C/A → PT/DEF/CEN/ATT? Utile se hai caricato le giornate prima del fix del parser.")) return;
    setRepairing(true);
    try {
      const { data } = await api.post("/admin/dev/repair-player-ratings-roles");
      toast.success(`Aggiornati ${data.modified} voti — Calciatori e Best Performance ora vedranno i dati`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore repair");
    } finally { setRepairing(false); }
  };

  const [diag, setDiag] = useState(null);
  const [diagBusy, setDiagBusy] = useState(false);
  const runDiag = async () => {
    setDiagBusy(true);
    try {
      const { data } = await api.get("/admin/dev/diagnose-performance");
      setDiag(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore diagnostica");
    } finally { setDiagBusy(false); }
  };

  const [rebuildBusy, setRebuildBusy] = useState(false);
  const [rebuildResult, setRebuildResult] = useState(null);
  const runRebuild = async () => {
    if (!window.confirm("Retrofit classifica: risincronizza matchdays.scores dai campionato_matches per rimettere in ordine la classifica anche dopo l'onboarding di nuovi coach. Continuare?")) return;
    setRebuildBusy(true);
    try {
      const { data } = await api.post("/admin/dev/rebuild-standings");
      setRebuildResult(data);
      toast.success(`Classifica ricalcolata · ${data.matchdays_rebuilt} giornate · ${data.standings_rows} righe`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore retrofit");
    } finally { setRebuildBusy(false); }
  };

  const hasArchive = (info?.total || 0) > 0;
  return (
    <div className={`rounded-2xl border-2 p-6 ${hasArchive ? "border-amber-300 bg-amber-50" : "border-slate-200 bg-slate-50"}`}
         data-testid="admin-restore-users">
      <div className="flex items-start gap-3">
        <Rocket className={`w-6 h-6 shrink-0 mt-1 ${hasArchive ? "text-amber-700" : "text-slate-400"}`} />
        <div className="flex-1">
          <div className={`display font-black text-lg ${hasArchive ? "text-amber-900" : "text-slate-500"}`}>
            Ripristino utenti dall'archivio
          </div>
          <p className={`text-sm mt-1 leading-relaxed ${hasArchive ? "text-amber-900/80" : "text-slate-500"}`}>
            Ogni volta che esegui il Go-Live Reset con "elimina utenti", gli utenti vengono <b>archiviati</b> in <code>users_archive</code> prima di essere cancellati. Da qui puoi ripristinarli in un click.
          </p>
          <div className="mt-3 text-sm">
            <div><b>Utenti archiviati:</b> {info.total || 0}</div>
            {info.archived_at && (
              <div className="text-xs text-slate-600">Archiviati il: {new Date(info.archived_at).toLocaleString("it-IT")}</div>
            )}
            {info.statuses && Object.keys(info.statuses).length > 0 && (
              <div className="text-xs text-slate-600 mt-1">
                Composizione: {Object.entries(info.statuses).map(([k, v]) => `${k}=${v}`).join(" · ")}
              </div>
            )}
          </div>
          <Button onClick={run} disabled={running || !hasArchive}
                  className="mt-3 bg-amber-600 hover:bg-amber-700 text-white disabled:bg-slate-300"
                  data-testid="restore-users-btn">
            {running ? "Ripristino…" : hasArchive ? `Ripristina ${info.total} utenti` : "Nessun archivio disponibile"}
          </Button>
          <div className="mt-4 pt-4 border-t border-amber-200/60">
            <div className="text-xs uppercase tracking-widest text-slate-500 font-bold">Fix retro-compatibile</div>
            <p className="text-xs text-slate-600 mt-1">
              Se hai caricato le giornate prima del fix del parser, i ruoli in <code>player_ratings</code> sono salvati come <b>P/D/C/A</b> (una lettera) e non matchano <code>asta_teams.players</code> (PT/DEF/CEN/ATT). Risultato: Calciatori vuoto, Best Performance vuoto.
            </p>
            <Button onClick={repairRoles} disabled={repairing}
                    className="mt-2 bg-slate-800 hover:bg-slate-900 text-white text-xs h-8 rounded-full"
                    data-testid="repair-roles-btn">
              {repairing ? "Aggiornamento…" : "Ricalcola ruoli player_ratings"}
            </Button>
            <Button onClick={runDiag} disabled={diagBusy}
                    className="mt-2 ml-2 bg-indigo-600 hover:bg-indigo-700 text-white text-xs h-8 rounded-full"
                    data-testid="diagnose-perf-btn">
              {diagBusy ? "Analisi…" : "Diagnostica Calciatori/Perf"}
            </Button>
            <Button onClick={runRebuild} disabled={rebuildBusy}
                    className="mt-2 ml-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs h-8 rounded-full"
                    data-testid="rebuild-standings-btn">
              <Trophy className="w-3.5 h-3.5 mr-1" />
              {rebuildBusy ? "Retrofit…" : "Retrofit Classifica"}
            </Button>
            {rebuildResult && (
              <div className="mt-3 rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-xs space-y-1" data-testid="rebuild-standings-result">
                <div><b>Giornate ricalcolate:</b> {rebuildResult.matchdays_rebuilt} · <b>Squadre in classifica:</b> {rebuildResult.standings_rows}</div>
                {rebuildResult.preview?.length > 0 && (
                  <div className="space-y-0.5 mt-2">
                    <div className="text-[10px] uppercase tracking-widest text-emerald-700 font-bold">Top 5 preview</div>
                    {rebuildResult.preview.map((s, i) => (
                      <div key={i} className="font-mono text-[10px]">
                        {i + 1}. <b>{s.team_name}</b> [{s.girone}] · {s.points}pt · {s.total_points}fv · {s.played} giornate
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            {diag && (
              <div className="mt-3 rounded-xl border border-slate-200 bg-white p-3 text-xs space-y-2" data-testid="diagnose-perf-result">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  <div><b>player_ratings totali:</b> {diag.player_ratings?.total_docs}</div>
                  <div><b>con fantavoto:</b> {diag.player_ratings?.with_fantavoto}</div>
                  <div><b>asta_teams:</b> {diag.asta_teams?.total_teams}</div>
                  <div><b>giocatori asta:</b> {diag.asta_teams?.total_players}</div>
                </div>
                <div className="pt-2 border-t border-slate-100">
                  <b>Match rate:</b> {diag.join_stats?.match_rate}% ·
                  Exact <b>{diag.join_stats?.matched_exact}</b> +
                  Fallback last-name <b>{diag.join_stats?.matched_fallback_lastname_team}</b> ·
                  Non matchati <b className="text-red-600">{diag.join_stats?.unmatched_count}</b>
                </div>
                <div className="text-slate-500">
                  <b>Ruoli in player_ratings:</b> {JSON.stringify(diag.player_ratings?.role_counts)}
                </div>
                {diag.unmatched_sample?.length > 0 && (
                  <details className="pt-2 border-t border-slate-100">
                    <summary className="cursor-pointer font-bold text-slate-700">
                      Campione non matchati ({diag.unmatched_sample.length})
                    </summary>
                    <div className="mt-1 max-h-64 overflow-y-auto space-y-1">
                      {diag.unmatched_sample.map((u, i) => (
                        <div key={i} className="font-mono text-[10px] bg-slate-50 p-1 rounded">
                          <b>{u.name}</b> ({u.role}) · {u.team} [{u.girone}] · key=<code>{u.search_key.name_norm}</code> · last=<code>{u.search_key.last_name}</code>
                        </div>
                      ))}
                    </div>
                  </details>
                )}
                {diag.player_ratings?.sample?.length > 0 && (
                  <details className="pt-2 border-t border-slate-100">
                    <summary className="cursor-pointer font-bold text-slate-700">
                      Campione player_ratings (10)
                    </summary>
                    <div className="mt-1 max-h-64 overflow-y-auto space-y-1">
                      {diag.player_ratings.sample.map((s, i) => (
                        <div key={i} className="font-mono text-[10px] bg-slate-50 p-1 rounded">
                          <b>{s.name}</b> ({s.role}) · {s.team_name} · fv={String(s.fantavoto)}
                        </div>
                      ))}
                    </div>
                  </details>
                )}
              </div>
            )}
          </div>
          {result && (
            <div className="mt-3 text-sm text-amber-900/90" data-testid="restore-users-result">
              Ripristinati <b>{result.restored}</b> · Saltati (già esistenti) <b>{result.skipped}</b> · Totale archivio {result.archive_total}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


function PopulateFromScratchCard() {
  const [wipe, setWipe] = useState(true);
  const [confirmText, setConfirmText] = useState("");
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    if (confirmText !== "POPOLA") {
      toast.error('Digita "POPOLA" per confermare');
      return;
    }
    setRunning(true);
    setResult(null);
    try {
      const { data } = await api.post(`/admin/dev/populate-from-scratch?wipe=${wipe}`);
      setResult(data);
      toast.success("Ambiente popolato con successo");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore populate-from-scratch");
    } finally {
      setRunning(false);
      setConfirmText("");
    }
  };

  return (
    <div className="rounded-2xl border-2 border-emerald-300 bg-emerald-50 p-6" data-testid="admin-populate-from-scratch">
      <div className="flex items-start gap-3">
        <Rocket className="w-6 h-6 text-emerald-700 shrink-0 mt-1" />
        <div className="flex-1">
          <div className="display font-black text-lg text-emerald-900">Popola tutto da zero (Produzione)</div>
          <p className="text-emerald-900/80 text-sm mt-1 leading-relaxed">
            One-click setup per l'ambiente di <b>Produzione</b> subito dopo il deploy. Esegue in sequenza:
            <br />1) (Opzionale) <b>Wipe</b> di tutti i dati (preserva solo l'admin)
            <br />2) Seed dei <b>32 asta_teams</b> (4 gironi × 8 squadre × 26 giocatori) dal file bundled
            <br />3) Simulazione <b>stagione completa</b>: 32 utenti, 32 giornate, 512 partite, ~30 interviste, ~70 gufate, supporters, player_ratings
            <br />4) Patch immagini uniche (scudetti, kit, coach, cover interviste)
            <br />5) Genera <b>6 Fantascommesse</b> con quote auto-calcolate
            <br />6) Simula <b>Coppe fine-stagione</b>: Maxi Girone (128 partite), Playoff Champions (8), Ottavi Champions (8), Ottavi Europa (8) e Brackets Champions/Europa 8-team
          </p>
          <div className="mt-4 flex flex-wrap gap-3 items-center">
            <label className="flex items-center gap-2 text-sm text-emerald-900 cursor-pointer" data-testid="populate-wipe-toggle">
              <input type="checkbox" checked={wipe} onChange={(e) => setWipe(e.target.checked)} className="w-4 h-4 accent-emerald-600" />
              <b>Wipe totale</b> prima di popolare (consigliato in Produzione)
            </label>
          </div>
          <div className="mt-3 flex flex-wrap gap-2 items-center">
            <Input value={confirmText} onChange={(e) => setConfirmText(e.target.value.toUpperCase())}
                   placeholder='Digita "POPOLA"' className="max-w-xs bg-white border-emerald-300"
                   data-testid="populate-confirm-input" />
            <Button onClick={run} disabled={running || confirmText !== "POPOLA"}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="populate-execute-btn">
              {running ? "Popolamento in corso…" : "🚀 Popola tutto da zero"}
            </Button>
          </div>
          {running && (
            <div className="mt-3 text-sm text-emerald-800">
              Operazione lunga (30–90s). Non chiudere la pagina.
            </div>
          )}
          {result?.summary && (
            <div className="mt-4" data-testid="populate-result">
              <div className="display font-bold text-sm text-emerald-900 mb-2">Riepilogo finale</div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                <StatBox label="Utenti" value={result.summary.users} color="#059669" />
                <StatBox label="Teams" value={result.summary.teams} color="#059669" />
                <StatBox label="Asta teams" value={result.summary.asta_teams} color="#059669" />
                <StatBox label="Giornate" value={result.summary.matchdays} color="#059669" />
                <StatBox label="Match" value={result.summary.matches} color="#059669" />
                <StatBox label="Interviste" value={result.summary.interviews} color="#059669" />
                <StatBox label="Gufate" value={result.summary.gufate} color="#059669" />
                <StatBox label="Supporter" value={result.summary.supporters} color="#059669" />
                <StatBox label="Voti giocatori" value={result.summary.player_ratings} color="#059669" />
                <StatBox label="Fantascommesse" value={result.summary.fanta_markets} color="#059669" />
                <StatBox label="Coppa Maxi" value={result.summary.coppa_maxi} color="#059669" />
                <StatBox label="Playoff" value={result.summary.coppa_playoff} color="#059669" />
                <StatBox label="Ottavi Ch." value={result.summary.coppa_ottavi} color="#059669" />
                <StatBox label="Ottavi Eur." value={result.summary.coppa_europa} color="#059669" />
                <StatBox label="Brackets" value={result.summary.brackets} color="#059669" />
              </div>
              {result.steps?.length > 0 && (
                <ul className="mt-3 text-xs text-emerald-900/80 space-y-1">
                  {result.steps.map((s, i) => (
                    <li key={i}>
                      ✔ <b>{s.step}</b>
                      {s.inserted != null && ` — inserted: ${s.inserted}`}
                      {s.skipped && ` — skipped (esistenti: ${s.existing})`}
                      {s.markets_created != null && ` — mercati: ${s.markets_created}`}
                      {s.error && ` — ⚠️ ${s.error}`}
                      {s.wiped && ` — collezioni azzerate: ${Object.keys(s.wiped).length}`}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function CleanupTab() {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [result, setResult] = useState(null);
  const [confirmed, setConfirmed] = useState("");
  const [backfilling, setBackfilling] = useState(false);
  const [backfillResult, setBackfillResult] = useState(null);

  const loadPreview = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/cleanup/non-asta/preview");
      setPreview(data);
    } catch (e) {
      toast.error("Errore preview");
    } finally { setLoading(false); }
  };

  useEffect(() => { loadPreview(); }, []); // eslint-disable-line

  const execute = async () => {
    if (confirmed !== "ELIMINA") { toast.error('Digita "ELIMINA" per confermare'); return; }
    setExecuting(true);
    try {
      const { data } = await api.post("/admin/cleanup/non-asta/execute");
      setResult(data);
      toast.success("Cleanup completato");
      await loadPreview();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore cleanup");
    } finally { setExecuting(false); setConfirmed(""); }
  };

  const backfillRosters = async () => {
    setBackfilling(true);
    try {
      const { data } = await api.post("/admin/users/backfill-rosters");
      setBackfillResult(data);
      toast.success(`Rose sincronizzate: ${data.updated} squadre aggiornate`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore sincronizzazione rose");
    } finally { setBackfilling(false); }
  };

  const simulateMarkets = async () => {
    try {
      const { data } = await api.post("/admin/simulate/fantascommesse");
      toast.success(`Fantascommesse create: ${data.markets_created} mercati`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore simulazione");
    }
  };

  const [normalizing, setNormalizing] = useState(false);
  const [normalizeResult, setNormalizeResult] = useState(null);
  const normalizeTeamNames = async () => {
    if (!window.confirm(
      "Normalizzare i nomi delle squadre in tutti i risultati?\n\n" +
      "Verranno riscritti i nomi in campionato_matches, giornata_results e player_ratings " +
      "usando il fuzzy match contro asta_teams (nomi canonici). Poi consolida le righe " +
      "duplicate e ricalcola gli score per matchday. Continuare?"
    )) return;
    setNormalizing(true); setNormalizeResult(null);
    try {
      const { data } = await api.post("/admin/cleanup/normalize-team-names");
      setNormalizeResult(data);
      const n = data.normalized || {};
      const d = data.dedupe_removed || {};
      toast.success(
        `Normalizzati: ${n.campionato_matches || 0} match · ${n.giornata_results || 0} risultati · ` +
        `${n.player_ratings || 0} voti. Dedupe: ${(d.campionato_matches || 0) + (d.giornata_results || 0)} righe.`
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore normalizzazione");
    } finally { setNormalizing(false); }
  };

  return (
    <div className="space-y-6" data-testid="admin-cleanup-tab">
      <PopulateFromScratchCard />
      <GoLiveResetCard />
      <RestoreUsersCard />
      {/* Normalize team names */}
      <div className="card-surface rounded-2xl p-6" data-testid="normalize-team-names-card">
        <div className="flex items-start gap-3 flex-wrap">
          <div className="w-10 h-10 rounded-xl bg-blue-600 text-white flex items-center justify-center shrink-0">
            <Wand2 className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="display font-bold text-xl">Normalizza nomi squadre</h3>
            <p className="text-slate-600 text-sm mt-2 leading-relaxed">
              Corregge i typo dell'AI (es. <b>LOS ANFELO - UN ESP</b> → <b>LOS ANGELO - UN ESPERTO</b>,
              <b> NEWS LOD BOYS</b> → <b>NEWELL'S LOD BOYS</b>) allineando <b>tutti i risultati</b> ai nomi canonici delle
              rose dell'asta. Al termine consolida le righe duplicate e ricalcola gli score di ogni giornata.
            </p>
            <Button
              onClick={normalizeTeamNames}
              disabled={normalizing}
              className="mt-4 rounded-full bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="normalize-team-names-btn"
            >
              <Wand2 className="w-4 h-4 mr-2" />
              {normalizing ? "Normalizzazione…" : "Normalizza nomi squadre"}
            </Button>
            {normalizeResult && (
              <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="normalize-result">
                <StatBox label="Match rinominati" value={normalizeResult.normalized?.campionato_matches ?? 0} color="#3b82f6" />
                <StatBox label="Risultati rinominati" value={normalizeResult.normalized?.giornata_results ?? 0} color="#3b82f6" />
                <StatBox label="Voti rinominati" value={normalizeResult.normalized?.player_ratings ?? 0} color="#3b82f6" />
                <StatBox label="Righe deduplicate" value={(normalizeResult.dedupe_removed?.campionato_matches ?? 0) + (normalizeResult.dedupe_removed?.giornata_results ?? 0)} color="#22c55e" />
                {Object.entries(normalizeResult.unresolved || {}).map(([coll, names]) => names.length > 0 && (
                  <div key={coll} className="col-span-2 md:col-span-4 text-xs text-amber-700 bg-amber-50 rounded-xl p-3 border border-amber-200">
                    <b>Non risolti in {coll}:</b> {names.slice(0, 10).join(" · ")}{names.length > 10 ? ` … (+${names.length - 10})` : ""}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="card-surface rounded-2xl p-6">
        <h3 className="display font-bold text-xl">Pulizia dati non-asta</h3>
        <p className="text-slate-600 text-sm mt-2 leading-relaxed">
          Rimuove <b>permanentemente</b> tutte le squadre, utenti, interviste, gufate, supporter e notifiche
          <b> non collegati</b> alle squadre importate dagli Excel delle aste. Verranno preservati:
          i 32 asta_teams, gli utenti con team match e l'utente admin.
        </p>
        {preview && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
            <StatBox label="Asta teams" value={preview.asta_teams} color="#22c55e" />
            <StatBox label="Utenti validi" value={preview.valid_users} color="#22c55e" />
            <StatBox label="Admin" value={preview.admin_users} color="#3b56e0" />
            <div />
            <StatBox label="Interviste" value={preview.interviews_orphans} color="#f97316" />
            <StatBox label="Teams" value={preview.teams_orphans} color="#f97316" />
            <StatBox label="Utenti" value={preview.users_orphans} color="#f97316" />
            <StatBox label="Gufate" value={preview.gufate_orphans} color="#f97316" />
            <StatBox label="Supporter" value={preview.supporters_orphans} color="#f97316" />
            <StatBox label="Notifiche" value={preview.notifications_orphans} color="#f97316" />
            <StatBox label="Campionato matches" value={preview.campionato_matches_orphans} color="#f97316" />
            <StatBox label="Matchday scores" value={preview.matchdays_score_entries_orphans} color="#f97316" />
            <StatBox label="Fantascommesse" value={preview.fanta_markets_orphans} color="#f97316" />
            <StatBox label="Puntate" value={preview.fanta_bets_orphans} color="#f97316" />
            <StatBox label="Tutto o Niente" value={preview.challenges_orphans} color="#f97316" />
          </div>
        )}
        <Button onClick={loadPreview} disabled={loading} className="mt-4" variant="outline" data-testid="cleanup-refresh">
          {loading ? "Aggiornamento…" : "Aggiorna anteprima"}
        </Button>
      </div>

      <div className="card-surface rounded-2xl p-6" data-testid="admin-backfill-rosters">
        <h3 className="display font-bold text-xl">Sincronizza Rose dagli asta_teams</h3>
        <p className="text-slate-600 text-sm mt-2 leading-relaxed">
          Retro-fix per gli utenti già approvati (prima del fix del <b>2026-02-11</b>): per ogni squadra
          il cui <code className="bg-slate-100 px-1.5 rounded">team_name</code> matcha un asta_team, copia
          la rosa dall'Excel dell'asta (29 giocatori tipici) nella sua area personale. Non sovrascrive
          rose già popolate. Sicuro da eseguire più volte (idempotente).
        </p>
        <div className="mt-4 flex gap-2 flex-wrap items-center">
          <Button onClick={backfillRosters} disabled={backfilling} className="btn-brand text-white" data-testid="backfill-rosters-btn">
            {backfilling ? "Sincronizzazione in corso…" : "Sincronizza rose ora"}
          </Button>
          <Button onClick={simulateMarkets} variant="outline" data-testid="simulate-markets-btn">
            Genera Fantascommesse
          </Button>
          {backfillResult && (
            <div className="text-sm text-slate-700">
              ✅ Aggiornate <b>{backfillResult.updated}</b> · No match: {backfillResult.skipped_no_asta_match} · Già presenti: {backfillResult.skipped_already_has_roster}
            </div>
          )}
        </div>
        {backfillResult?.details?.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {backfillResult.details.map((d, i) => (
              <span key={i} className="text-[11px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 border border-emerald-200">
                {d.team_name} · {d.players}
              </span>
            ))}
          </div>
        )}
      </div>

      <div className="rounded-2xl border-2 border-red-300 bg-red-50 p-6">
        <div className="flex items-start gap-3">
          <Trash2 className="w-6 h-6 text-red-600 shrink-0 mt-1" />
          <div className="flex-1">
            <div className="display font-black text-lg text-red-900">Zona pericolosa</div>
            <p className="text-red-800 text-sm mt-1">
              Questa operazione <b>non è reversibile</b>. Digita <code className="bg-red-100 px-1.5 py-0.5 rounded font-mono text-red-900">ELIMINA</code> qui sotto per attivare il bottone.
            </p>
            <div className="mt-3 flex gap-2 flex-wrap">
              <Input value={confirmed} onChange={(e) => setConfirmed(e.target.value.toUpperCase())}
                     placeholder='Digita "ELIMINA"' className="max-w-xs bg-white border-red-300"
                     data-testid="cleanup-confirm-input" />
              <Button onClick={execute} disabled={executing || confirmed !== "ELIMINA"}
                      className="bg-red-600 hover:bg-red-700 text-white" data-testid="cleanup-execute-btn">
                {executing ? "Eliminazione in corso…" : "Elimina dati non-asta"}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {result && (
        <div className="card-surface rounded-2xl p-6" data-testid="cleanup-result">
          <div className="display font-bold text-lg mb-3">Report ultimo cleanup</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatBox label="Interviste" value={result.interviews_deleted} color="#ef4444" />
            <StatBox label="Teams" value={result.teams_deleted} color="#ef4444" />
            <StatBox label="Utenti" value={result.users_deleted} color="#ef4444" />
            <StatBox label="Gufate" value={result.gufate_deleted} color="#ef4444" />
            <StatBox label="Supporter" value={result.supporters_deleted} color="#ef4444" />
            <StatBox label="Notifiche" value={result.notifications_deleted} color="#ef4444" />
            <StatBox label="Push subs" value={result.subscriptions_deleted} color="#ef4444" />
            <StatBox label="Match campionato" value={result.campionato_matches_deleted} color="#ef4444" />
            <StatBox label="Score matchday" value={result.matchdays_score_entries_removed} color="#ef4444" />
            <StatBox label="Puntate" value={result.fanta_bets_deleted} color="#ef4444" />
            <StatBox label="Tutto o Niente" value={result.challenges_deleted} color="#ef4444" />
            <StatBox label="Utenti preservati" value={result.preserved_users} color="#22c55e" />
          </div>
        </div>
      )}
    </div>
  );
}

export default function AdminPanel() {
  return (
    <div className="App">
      <Header />
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8 space-y-6">
        <div className="fade-up">
          <div className="text-xs uppercase tracking-widest text-slate-500">Console amministratore</div>
          <h1 className="display font-black text-4xl md:text-5xl mt-1">
            Fantacampionato <span style={{ color: "var(--brand)" }}>FIFE 2026/27</span>
          </h1>
        </div>
        <Tabs defaultValue="users">
          <TabsList className="bg-slate-50 border border-slate-200 flex overflow-x-auto whitespace-nowrap h-auto p-1 gap-1 max-w-full [&>button]:shrink-0 [&::-webkit-scrollbar]:h-1">
            <TabsTrigger value="users" data-testid="admin-tab-users">Utenti</TabsTrigger>
            <TabsTrigger value="squadre" data-testid="admin-tab-squadre">Squadre</TabsTrigger>
            <TabsTrigger value="asta" data-testid="admin-tab-asta">Aste</TabsTrigger>
            <TabsTrigger value="matchday" data-testid="admin-tab-matchday">Giornate</TabsTrigger>
            <TabsTrigger value="calendario" data-testid="admin-tab-calendario">Calendario</TabsTrigger>
            <TabsTrigger value="coppa" data-testid="admin-tab-coppa">Coppa</TabsTrigger>
            <TabsTrigger value="interviste" data-testid="admin-tab-interviste">Interviste</TabsTrigger>
            <TabsTrigger value="markets" data-testid="admin-tab-markets">Mercati</TabsTrigger>
            <TabsTrigger value="push" data-testid="admin-tab-push">Push</TabsTrigger>
            <TabsTrigger value="supporter" data-testid="admin-tab-supporter">Supporter</TabsTrigger>
            <TabsTrigger value="cleanup" data-testid="admin-tab-cleanup">Cleanup</TabsTrigger>
          </TabsList>
          <TabsContent value="users" className="mt-6"><UsersTab /></TabsContent>
          <TabsContent value="squadre" className="mt-6"><SquadreAdminTab /></TabsContent>
          <TabsContent value="asta" className="mt-6"><AstaTab /></TabsContent>
          <TabsContent value="matchday" className="mt-6"><MatchdayTab /></TabsContent>
          <TabsContent value="calendario" className="mt-6"><CalendarioTab /></TabsContent>
          <TabsContent value="coppa" className="mt-6"><CoppaMaxiTab /></TabsContent>
          <TabsContent value="interviste" className="mt-6"><IntervisteTab /></TabsContent>
          <TabsContent value="markets" className="mt-6"><MarketsTab /></TabsContent>
          <TabsContent value="push" className="mt-6"><PushTab /></TabsContent>
          <TabsContent value="supporter" className="mt-6"><SupporterTab /></TabsContent>
          <TabsContent value="cleanup" className="mt-6"><CleanupTab /></TabsContent>
        </Tabs>
      </div>
      <Footer />
    </div>
  );
}

/* ============ SQUADRE (asta) ADMIN TAB ============ */
function SquadreAdminTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("ALL");

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/admin/asta-teams/detail"); setRows(data || []); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = filter === "ALL" ? rows : rows.filter((r) => r.girone === filter);
  const stats = {
    total: rows.length,
    onboarded: rows.filter((r) => r.onboarding_status === "onboarded").length,
    invited: rows.filter((r) => r.onboarding_status === "invited").length,
    pending: rows.filter((r) => r.onboarding_status === "pending").length,
  };

  if (loading) return <div className="text-slate-500">Caricamento…</div>;
  if (rows.length === 0) {
    return (
      <div className="card-surface rounded-2xl p-8 text-center text-slate-500" data-testid="squadre-empty">
        Nessuna squadra caricata. Carica prima le aste dalla tab <b>Aste</b>.
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="admin-squadre-tab">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-4">
        <StatBox label="Totale squadre" value={stats.total} color="#0f172a" />
        <StatBox label="Onboarded" value={stats.onboarded} color="#16a34a" />
        <StatBox label="Link inviato" value={stats.invited} color="#ea580c" />
        <StatBox label="Da invitare" value={stats.pending} color="#dc2626" />
      </div>

      <div className="flex flex-wrap gap-1.5">
        {["ALL", "A", "B", "C", "D"].map((g) => (
          <button key={g} type="button" onClick={() => setFilter(g)}
                  data-testid={`squadre-filter-${g}`}
                  className={`h-9 px-3 rounded-full text-[11px] font-black uppercase tracking-widest inline-flex items-center gap-1.5
                    ${filter === g ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
            {g !== "ALL" && <GironeBadge girone={g} size={14} />}
            {g === "ALL" ? "Tutti" : g}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {filtered.map((r) => <TeamAdminCard key={r.id} row={r} onChange={load} />)}
      </div>
    </div>
  );
}

function TeamAdminCard({ row, onChange }) {
  const [busy, setBusy] = useState(null);
  const shieldRef = React.useRef(null);
  const jerseyRef = React.useRef(null);
  const coachRef = React.useRef(null);

  const [renaming, setRenaming] = useState(false);

  const renameTeam = async () => {
    const proposed = window.prompt(
      `Rinomina squadra "${row.team_name}"\n\n` +
      `L'ID univoco resterà invariato: tutti i risultati storici, i voti e le classifiche continueranno a puntare a questa squadra. ` +
      `Le altre collezioni verranno aggiornate automaticamente con il nuovo nome.`,
      row.team_name || ""
    );
    if (proposed === null) return;
    const newName = proposed.trim();
    if (!newName || newName === row.team_name) return;
    setRenaming(true);
    try {
      const { data } = await api.post(`/admin/asta-teams/${row.id}/rename`, { team_name: newName });
      const u = data.updated || {};
      toast.success(
        `Rinominata "${data.old_name}" → "${data.new_name}" · ` +
        `${u.campionato_matches || 0} match, ${u.player_ratings || 0} voti, ` +
        `${u.giornata_results || 0} risultati aggiornati`
      );
      await onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rinomina fallita");
    } finally { setRenaming(false); }
  };

  const onboardingUrl = row.onboarding_token
    ? `${window.location.origin}/onboarding/${row.onboarding_token}`
    : null;

  const upload = async (kind, file) => {
    if (!file) return;
    setBusy(kind);
    try {
      const fd = new FormData(); fd.append("file", file);
      await api.post(`/admin/asta-teams/${row.id}/${kind}`, fd);
      toast.success(`${kind === "shield" ? "Stemma" : kind === "jersey" ? "Kit" : "Coach"} caricato`);
      await onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload fallito");
    } finally { setBusy(null); }
  };

  const generateLink = async () => {
    setBusy("gen");
    try {
      await api.post(`/admin/asta-teams/${row.id}/generate-onboarding-token`);
      toast.success("Link invito generato");
      await onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore generazione link");
    } finally { setBusy(null); }
  };

  const revokeLink = async () => {
    if (!window.confirm("Revocare il link di invito? Il link diventerà inutilizzabile.")) return;
    setBusy("revoke");
    try {
      await api.post(`/admin/asta-teams/${row.id}/revoke-onboarding-token`);
      toast.success("Link revocato");
      await onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore revoca");
    } finally { setBusy(null); }
  };

  const copyLink = async () => {
    if (!onboardingUrl) return;
    try {
      await navigator.clipboard.writeText(onboardingUrl);
      toast.success("Link copiato negli appunti");
    } catch {
      toast.error("Impossibile copiare — copia manualmente");
    }
  };

  const statusColor = row.onboarding_status === "onboarded" ? "emerald"
                    : row.onboarding_status === "invited" ? "amber" : "slate";
  const statusLabel = row.onboarding_status === "onboarded"
    ? `Onboarded: ${row.linked_user?.full_name || row.linked_user?.email}`
    : row.onboarding_status === "invited" ? "Link generato · in attesa" : "Da invitare";

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 space-y-3"
         data-testid={`squadra-card-${row.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <GironeBadge girone={row.girone} size={32} />
          <div className="min-w-0">
            <div className="display font-black text-lg truncate flex items-center gap-2">
              <span className="truncate">{row.team_name}</span>
              <button type="button" onClick={renameTeam} disabled={renaming}
                      title="Rinomina squadra (l'ID resta lo stesso)"
                      className="shrink-0 inline-flex items-center justify-center h-6 w-6 rounded-full text-slate-500 hover:bg-slate-100 hover:text-slate-900 transition"
                      data-testid={`squadra-rename-${row.id}`}>
                <Pencil className="w-3 h-3" />
              </button>
            </div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500">
              Girone {row.girone} · {row.players_count} calciatori · <span className="font-mono normal-case tracking-normal">ID {row.id?.slice(0, 6)}</span>
            </div>
          </div>
        </div>
        <Badge className={`bg-${statusColor}-100 text-${statusColor}-700 border-${statusColor}-200 whitespace-nowrap text-[10px]`}>
          {statusLabel}
        </Badge>
      </div>

      {/* Assets */}
      <div className="grid grid-cols-3 gap-2">
        {[
          { kind: "shield", label: "Stemma", ref: shieldRef, path: row.shield_path },
          { kind: "jersey", label: "Kit", ref: jerseyRef, path: row.jersey_path },
          { kind: "coach", label: "Mister", ref: coachRef, path: row.coach_path },
        ].map((a) => (
          <div key={a.kind}
               className="rounded-xl border border-slate-200 bg-slate-50 aspect-square flex flex-col items-center justify-center p-2 relative overflow-hidden">
            {a.path ? (
              <img src={publicFileUrl(a.path)} alt={a.label} className="w-full h-full object-contain" />
            ) : (
              <div className="text-slate-300 text-[10px] uppercase tracking-widest text-center">
                {a.label}<br />non caricato
              </div>
            )}
            <input ref={a.ref} type="file" accept="image/*" className="hidden"
                   onChange={(e) => upload(a.kind, e.target.files?.[0])}
                   data-testid={`squadra-upload-${a.kind}-${row.id}`} />
            <button type="button" onClick={() => a.ref.current?.click()}
                    disabled={busy === a.kind}
                    className="absolute bottom-1 right-1 bg-slate-900/80 hover:bg-slate-900 text-white text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded-full">
              {busy === a.kind ? "…" : (a.path ? "Cambia" : "Carica")}
            </button>
          </div>
        ))}
      </div>

      {/* Onboarding link */}
      {row.onboarding_status === "onboarded" ? (
        <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-3 text-xs" data-testid={`squadra-onboarded-${row.id}`}>
          <div className="flex items-center gap-2 text-emerald-900">
            <CheckCircle2 className="w-4 h-4" />
            <b>{row.linked_user?.full_name || "Fantallenatore"}</b> ha completato l'onboarding
          </div>
          <div className="text-emerald-800/80 mt-1">
            {row.linked_user?.email} {row.linked_user?.phone ? `· ${row.linked_user.phone}` : ""}
          </div>
        </div>
      ) : row.onboarding_status === "invited" ? (
        <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 space-y-2" data-testid={`squadra-invited-${row.id}`}>
          <div className="flex items-center gap-2 text-amber-900 text-xs">
            <Link2 className="w-4 h-4" /> <b>Link invito attivo</b>
          </div>
          <div className="flex gap-2">
            <Input value={onboardingUrl} readOnly onClick={(e) => e.target.select()}
                   className="bg-white text-xs" data-testid={`squadra-link-${row.id}`} />
            <Button type="button" onClick={copyLink} className="btn-brand rounded-full h-9 shrink-0"
                    data-testid={`squadra-copy-${row.id}`}>
              <Copy className="w-3.5 h-3.5 mr-1" /> Copia
            </Button>
          </div>
          <div className="flex justify-between items-center gap-2">
            <button type="button" onClick={generateLink} disabled={busy === "gen"}
                    className="text-[10px] uppercase tracking-widest font-bold text-amber-900 underline decoration-dotted">
              Rigenera link
            </button>
            <button type="button" onClick={revokeLink} disabled={busy === "revoke"}
                    className="text-[10px] uppercase tracking-widest font-bold text-red-700 underline decoration-dotted inline-flex items-center gap-1"
                    data-testid={`squadra-revoke-${row.id}`}>
              <ShieldOff className="w-3 h-3" /> Revoca
            </button>
          </div>
        </div>
      ) : (
        <Button type="button" onClick={generateLink} disabled={busy === "gen"}
                className="btn-brand rounded-full w-full h-10"
                data-testid={`squadra-generate-${row.id}`}>
          <Link2 className="w-4 h-4 mr-1" />
          {busy === "gen" ? "Genero link…" : "Genera link di invito"}
        </Button>
      )}
    </div>
  );
}


function StatBox({ label, value, color }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
      <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
      <div className="display font-black text-2xl tabular-nums" style={{ color }}>{value}</div>
    </div>
  );
}


/* ============ USERS TAB ============ */
function UsersTab() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/admin/users"); setUsers(data); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = users.filter((u) => {
    if (filter === "all") return true;
    if (filter === "pending") return u.status === "pending";
    if (filter === "approved") return u.status === "approved";
    return u.girone === filter;
  });
  const counts = {
    all: users.length,
    pending: users.filter((u) => u.status === "pending").length,
    approved: users.filter((u) => u.status === "approved").length,
    ...Object.fromEntries(GIRONI.map((g) => [g, users.filter((u) => u.girone === g).length])),
  };

  return (
    <>
      <div className="grid grid-cols-4 gap-2 md:gap-4 mb-4">
        {[
          { k: "all", label: "Tutti" }, { k: "pending", label: "Pending" },
          { k: "approved", label: "Approvati" }, { k: "A", label: "Girone A" },
        ].map((s) => (
          <button key={s.k} onClick={() => setFilter(s.k)}
                  className={`card-surface rounded-xl px-3 py-2 text-left ${filter === s.k ? "glow-brand" : ""}`}
                  data-testid={`filter-${s.k}`}>
            <div className="text-[10px] uppercase tracking-widest text-slate-500">{s.label}</div>
            <div className="display font-black text-xl">{counts[s.k] || 0}</div>
          </button>
        ))}
      </div>
      <div className="flex flex-wrap gap-2 mb-4">
        {GIRONI.map((g) => (
          <button key={g} onClick={() => setFilter(g)}
                  className={`glass rounded-full px-3 py-1 text-xs ${filter === g ? "text-white" : "text-slate-600"}`}
                  style={filter === g ? { background: "var(--brand)" } : {}}>
            Girone {g} · {counts[g] || 0}
          </button>
        ))}
      </div>
      {loading ? <div className="text-slate-500">Caricamento...</div> :
       filtered.length === 0 ? <div className="card-surface rounded-2xl p-8 text-center text-slate-500">Nessun utente in questo filtro.</div> :
       <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
         {filtered.map((u) => <UserCard key={u.id} u={u} onChange={load} />)}
       </div>}
    </>
  );
}

function UserCard({ u, onChange }) {
  const [teamName, setTeamName] = useState(u.team?.team_name || `Squadra ${u.full_name.split(" ")[0]}`);
  const [busy, setBusy] = useState(false);
  const [gufi, setGufi] = useState(u.gufi_wallet ?? 3);
  const [astaTeams, setAstaTeams] = useState([]);
  const [selectedAstaId, setSelectedAstaId] = useState("");
  const shieldRef = useRef(null); const jerseyRef = useRef(null); const rosterRef = useRef(null);

  // Carica le asta_teams del girone dell'utente (per il dropdown di assegnazione)
  useEffect(() => {
    if (!u.girone) return;
    api.get(`/admin/asta/teams-list?girone=${u.girone}`)
      .then(({ data }) => {
        setAstaTeams(data || []);
        // Preseleziona la squadra corrente se già assegnata, altrimenti la prima libera
        const currentNorm = (u.team?.team_name || "").trim().replace(/\.+$/, "").trim().toUpperCase();
        const current = (data || []).find(
          (a) => (a.team_name || "").trim().replace(/\.+$/, "").trim().toUpperCase() === currentNorm
        );
        if (current) setSelectedAstaId(current.id);
        else {
          const firstFree = (data || []).find((a) => !a.assigned_user_id);
          if (firstFree) {
            setSelectedAstaId(firstFree.id);
            setTeamName(firstFree.team_name);
          }
        }
      })
      .catch(() => setAstaTeams([]));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [u.girone, u.id]);

  const selectedAsta = astaTeams.find((a) => a.id === selectedAstaId);

  const approve = async () => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("team_name", (selectedAsta?.team_name) || teamName);
      await api.post(`/admin/users/${u.id}/approve`, fd);
      toast.success(`${u.full_name} approvato → ${selectedAsta?.team_name || teamName}`);
      onChange();
    } catch { toast.error("Errore approvazione"); } finally { setBusy(false); }
  };
  const reassignAstaTeam = async () => {
    if (!selectedAstaId) return toast.error("Seleziona una squadra");
    if (!window.confirm(`Associare ${u.full_name} → ${selectedAsta?.team_name}? Eventuale associazione precedente viene rimossa.`)) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("asta_team_id", selectedAstaId);
      const { data } = await api.post(`/admin/users/${u.id}/assign-asta-team`, fd);
      toast.success(`Associato a ${data.team_name} · ${data.roster_players} giocatori`);
      setTeamName(data.team_name);
      onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore associazione");
    } finally { setBusy(false); }
  };
  const updateTeam = async () => {
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("team_name", teamName);
      await api.post(`/admin/users/${u.id}/team`, fd);
      toast.success("Squadra aggiornata"); onChange();
    } catch { toast.error("Errore aggiornamento"); } finally { setBusy(false); }
  };
  const uploadImage = async (kind, file) => {
    if (!file) return; setBusy(true);
    try {
      const fd = new FormData(); fd.append(kind, file);
      await api.post(`/admin/users/${u.id}/team`, fd);
      toast.success(`${kind === "shield" ? "Stemma" : "Maglia"} caricato`); onChange();
    } catch { toast.error("Upload fallito"); } finally { setBusy(false); }
  };
  const uploadRoster = async (file) => {
    if (!file) return; setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post(`/admin/users/${u.id}/roster`, fd);
      toast.success(`Rosa caricata: ${data.count} giocatori`); onChange();
    } catch (e) { toast.error(e.response?.data?.detail || "Excel non valido"); } finally { setBusy(false); }
  };
  const setGufiWallet = async () => {
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("amount", String(gufi));
      await api.post(`/admin/users/${u.id}/gufi`, fd);
      toast.success("Gufi aggiornati"); onChange();
    } finally { setBusy(false); }
  };

  return (
    <div className="card-surface rounded-2xl p-5" data-testid={`user-card-${u.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="display font-bold text-lg truncate">{u.full_name}</div>
          <div className="text-xs text-slate-500 truncate">{u.email} · {u.phone}</div>
          <div className="flex gap-1 mt-1">
            <Badge variant="outline" className="border-slate-200 text-slate-600">Girone {u.girone}</Badge>
            <Badge className={u.status === "approved" ? "bg-[var(--brand)] text-white" : "bg-slate-100 text-slate-900"}>{u.status}</Badge>
          </div>
        </div>
        <div className="flex items-center gap-1 text-sm text-slate-600"><GufoIcon size={16} /> {u.gufi_wallet ?? 0}</div>
      </div>
      <div className="mt-4 space-y-3">
        {/* Selettore asta_team del girone */}
        <div>
          <Label className="text-slate-600 text-xs">Squadra dell'asta (Girone {u.girone})</Label>
          <Select value={selectedAstaId} onValueChange={(v) => {
            setSelectedAstaId(v);
            const a = astaTeams.find((x) => x.id === v);
            if (a) setTeamName(a.team_name);
          }}>
            <SelectTrigger className="mt-1 bg-slate-50 border-slate-200" data-testid={`asta-select-${u.id}`}>
              <SelectValue placeholder="Seleziona squadra dell'asta…" />
            </SelectTrigger>
            <SelectContent>
              {astaTeams.length === 0 && (
                <div className="px-3 py-2 text-xs text-slate-400">Nessun asta_team per il girone {u.girone}. Carica prima il file dell'asta.</div>
              )}
              {astaTeams.map((a) => {
                const takenByOther = a.assigned_user_id && a.assigned_user_id !== u.id;
                return (
                  <SelectItem key={a.id} value={a.id} data-testid={`asta-opt-${a.id}`}>
                    <span className={takenByOther ? "text-slate-400 line-through" : ""}>
                      {a.team_name}
                    </span>
                    {a.assigned_user_id === u.id && <span className="ml-2 text-[10px] text-emerald-600 font-bold">· TU</span>}
                    {takenByOther && <span className="ml-2 text-[10px] text-amber-600 font-bold">· già assegnata</span>}
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-slate-600 text-xs">Nome squadra (editabile)</Label>
          <Input value={teamName} onChange={(e) => setTeamName(e.target.value)} className="bg-slate-50 border-slate-200 mt-1" />
        </div>
        {u.status !== "approved"
          ? <Button onClick={approve} disabled={busy || !selectedAstaId} className="btn-brand w-full rounded-full" data-testid={`approve-${u.id}`}>
              <CheckCircle2 className="w-4 h-4 mr-2" /> Approva & associa a "{selectedAsta?.team_name || teamName}"</Button>
          : (
            <div className="grid grid-cols-2 gap-2">
              <Button onClick={updateTeam} disabled={busy} variant="outline" className="rounded-full border-slate-300">Aggiorna nome</Button>
              <Button onClick={reassignAstaTeam} disabled={busy || !selectedAstaId} className="btn-brand rounded-full"
                      data-testid={`reassign-${u.id}`}>
                Associa/Cambia asta
              </Button>
            </div>
          )}
        <div className="grid grid-cols-2 gap-2">
          <input ref={shieldRef} type="file" accept="image/*" className="hidden" onChange={(e) => uploadImage("shield", e.target.files?.[0])} />
          <input ref={jerseyRef} type="file" accept="image/*" className="hidden" onChange={(e) => uploadImage("jersey", e.target.files?.[0])} />
          <Button variant="outline" onClick={() => shieldRef.current?.click()} disabled={busy || u.status !== "approved"} className="border-slate-300 rounded-full"><ImageIcon className="w-4 h-4 mr-2" /> Stemma</Button>
          <Button variant="outline" onClick={() => jerseyRef.current?.click()} disabled={busy || u.status !== "approved"} className="border-slate-300 rounded-full"><ImageIcon className="w-4 h-4 mr-2" /> Maglia</Button>
        </div>
        <input ref={rosterRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={(e) => uploadRoster(e.target.files?.[0])} />
        <Button onClick={() => rosterRef.current?.click()} disabled={busy || u.status !== "approved"} variant="outline" className="w-full rounded-full border-slate-300">
          <FileSpreadsheet className="w-4 h-4 mr-2" /> Carica rosa Excel
        </Button>
        {u.team?.roster?.length ? <div className="text-xs text-slate-500">Rosa: {u.team.roster.length} giocatori</div> : null}
        <div className="flex items-end gap-2">
          <div className="flex-1">
            <Label className="text-slate-600 text-xs">Wallet Gufi</Label>
            <Input type="number" min={0} value={gufi} onChange={(e) => setGufi(parseInt(e.target.value || "0", 10))} className="bg-slate-50 border-slate-200 mt-1" />
          </div>
          <Button onClick={setGufiWallet} disabled={busy} className="rounded-full">Aggiorna</Button>
        </div>
      </div>
    </div>
  );
}

/* ============ MATCHDAY TAB ============ */
/* ============ MATCHDAY TAB ============ */
/* ============ CALENDARIO TAB ============ */
function CalendarioTab() {
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState({});
  const [previewResult, setPreviewResult] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/calendario/summary");
      setSummary(data || {});
    } catch (e) {
      toast.error("Errore caricamento summary");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const upload = async (g, file) => {
    if (!file) return;
    setUploading((s) => ({ ...s, [g]: true }));
    const fd = new FormData();
    fd.append("girone", g);
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/calendario/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreviewResult((s) => ({ ...s, [g]: data }));
      toast.success(`Calendario Girone ${g} caricato: ${data.matches} partite × ${data.giornate} giornate`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Errore upload Girone ${g}`);
    } finally {
      setUploading((s) => ({ ...s, [g]: false }));
    }
  };

  const remove = async (g) => {
    if (!window.confirm(`Eliminare il calendario del Girone ${g}?`)) return;
    try {
      await api.delete(`/admin/calendario/${g}`);
      toast.success(`Calendario Girone ${g} eliminato`);
      setPreviewResult((s) => { const c = { ...s }; delete c[g]; return c; });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore eliminazione");
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-calendario-tab">
      <div className="rounded-2xl border-2 border-violet-300 bg-violet-50 p-5" data-testid="calendario-simulate-panel">
        <div className="flex items-start gap-3 flex-wrap">
          <Dice5 className="w-6 h-6 text-violet-700 shrink-0 mt-1" />
          <div className="flex-1 min-w-0">
            <div className="display font-black text-lg text-violet-900">Simulazione calendari</div>
            <p className="text-violet-900/80 text-sm mt-1">
              Genera in un click un calendario round-robin doppio (andata + ritorno) per ciascun girone
              su <b>32 giornate</b>. Sostituisce integralmente il calendario esistente. Il pulsante di azzeramento
              cancella tutti i calendari così da poter ricaricare i file Excel da zero.
            </p>
          </div>
        </div>
        <div className="mt-4 flex gap-2 flex-wrap">
          <Button onClick={async () => {
            if (!window.confirm("Genero calendari casuali per tutti e 4 i gironi (32 giornate)?")) return;
            try {
              const { data } = await api.post("/admin/calendario/simulate");
              toast.success(`Generati ${data.matches} match × ${data.giornate} giornate × ${data.gironi} gironi`);
              load();
            } catch (e) { toast.error(e?.response?.data?.detail || "Errore simulazione"); }
          }} className="bg-violet-600 hover:bg-violet-700 text-white rounded-full" data-testid="simulate-calendario-btn">
            <Dice5 className="w-4 h-4 mr-1" /> 🎲 Genera calendari random (32 giornate)
          </Button>
          <Button onClick={async () => {
            if (!window.confirm("Confermi l'azzeramento di TUTTI i calendari dei 4 gironi?")) return;
            try {
              const { data } = await api.post("/admin/calendario/reset-all");
              toast.success(`Azzerati ${data.deleted} match totali`);
              load();
            } catch (e) { toast.error(e?.response?.data?.detail || "Errore azzeramento"); }
          }} variant="outline" className="border-red-300 text-red-700 hover:bg-red-50 rounded-full"
              data-testid="reset-calendario-btn">
            <Trash2 className="w-4 h-4 mr-1" /> Azzera calendario campionato
          </Button>
        </div>
      </div>

      <div className="card-surface rounded-2xl p-6">
        <div className="flex items-start gap-3">
          <CalendarDays className="w-6 h-6 text-slate-700 shrink-0 mt-1" />
          <div>
            <h3 className="display font-bold text-xl">Calendario del Campionato</h3>
            <p className="text-slate-600 text-sm mt-1">
              Carica il file Excel del calendario di ogni girone (uno per volta). Il formato accettato è
              quello standard di <b>Fantacalcio.it</b> (foglio "Calendario" con blocchi "Nª Giornata lega").
              Il caricamento <b>sostituisce</b> il calendario esistente del girone selezionato.
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {["A", "B", "C", "D"].map((g) => {
          const s = summary[g] || { matches: 0, giornate: 0, teams: 0 };
          const pr = previewResult[g];
          return (
            <div key={g} className="rounded-2xl border border-slate-200 bg-white p-5"
                 data-testid={`calendario-card-${g}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center font-black text-white"
                       style={{ background: {A:"#ef4444",B:"#3b82f6",C:"#10b981",D:"#a855f7"}[g] }}>
                    {g}
                  </div>
                  <div>
                    <div className="display font-bold text-lg text-slate-900">Girone {g}</div>
                    <div className="text-xs text-slate-500">
                      {loading ? "…" :
                        s.matches > 0
                          ? `${s.matches} partite · ${s.giornate} giornate · ${s.teams} squadre`
                          : "Nessun calendario caricato"}
                    </div>
                  </div>
                </div>
                {s.matches > 0 && (
                  <Button variant="outline" size="sm" onClick={() => remove(g)}
                          className="border-red-200 text-red-600 hover:bg-red-50"
                          data-testid={`calendario-delete-${g}`}>
                    Elimina
                  </Button>
                )}
              </div>
              <div className="mt-4">
                <label className="block">
                  <span className="text-sm font-medium text-slate-700">
                    {s.matches > 0 ? "Sostituisci con nuovo file" : "Carica calendario (.xlsx)"}
                  </span>
                  <input
                    type="file"
                    accept=".xlsx"
                    disabled={uploading[g]}
                    onChange={(e) => upload(g, e.target.files?.[0])}
                    className="mt-2 block w-full text-sm text-slate-700 file:mr-3 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-slate-100 file:text-slate-900 hover:file:bg-slate-200 disabled:opacity-50"
                    data-testid={`calendario-upload-${g}`}
                  />
                </label>
                {uploading[g] && (
                  <div className="mt-2 text-xs text-slate-500">Caricamento in corso…</div>
                )}
                {pr && (
                  <div className="mt-3 text-xs text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-lg p-2"
                       data-testid={`calendario-preview-${g}`}>
                    ✔ {pr.matches} partite × {pr.giornate} giornate.
                    Squadre: {(pr.team_names || []).join(", ")}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}


function MatchdayTab() {
  return (
    <div className="space-y-6">
      <SimulateScoresPanel />
      <KickoffSchedulerCard />
      <MatchdayUploadPanel />
      <MatchdayManualPanel />
    </div>
  );
}

// Formatta una data ISO (UTC o con TZ) come "gio 20 set 18:30" nel fuso locale.
function _fmtKickoff(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleString("it-IT", {
      weekday: "short", day: "2-digit", month: "short",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

// Converte un input datetime-local ("YYYY-MM-DDTHH:mm") interpretato come
// Europe/Rome in un ISO string con TZ. Approccio semplice: sfrutta il fuso
// del browser (che per l'admin italiano è quasi sempre Europe/Rome).
function _localToIso(localVal) {
  if (!localVal) return null;
  const d = new Date(localVal);
  if (isNaN(d.getTime())) return null;
  return d.toISOString();
}

// Converte un ISO UTC in stringa "YYYY-MM-DDTHH:mm" per il fuso del browser,
// così l'input datetime-local mostra l'orario corretto.
function _isoToLocal(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function KickoffSchedulerCard() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [matchday, setMatchday] = useState(1);
  const [when, setWhen] = useState("");
  const [saving, setSaving] = useState(false);
  const [editingMd, setEditingMd] = useState(null);
  const [editingVal, setEditingVal] = useState("");
  const [suggesting, setSuggesting] = useState(false);
  const [suggestion, setSuggestion] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/markets/kickoffs");
      setRows(data?.rows || []);
    } catch (e) {
      // silenzioso: se nessun mercato esiste, la tabella resta vuota
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const suggest = async (md) => {
    setSuggesting(true); setSuggestion(null);
    try {
      const { data } = await api.get(`/admin/markets/kickoffs/suggest?matchday=${md}`);
      const local = _isoToLocal(data.kickoff_at);
      setWhen(local);
      setSuggestion(data);
      toast.success(`Suggerito da Serie A ${data.season}: ${data.first_match.home} vs ${data.first_match.away}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Nessun suggerimento disponibile");
    } finally { setSuggesting(false); }
  };

  const save = async (md, localValue) => {
    const iso = _localToIso(localValue);
    if (!iso) return toast.error("Data/ora non valida");
    setSaving(true);
    try {
      const { data } = await api.post(`/admin/markets/${md}/kickoff`, { kickoff_at: iso });
      if (data?.updated > 0) {
        toast.success(`Kickoff G${md} impostato · aggiornati ${data.updated} mercati`);
      } else {
        toast.warning(`G${md}: nessun mercato 1X2 aperto da aggiornare`);
      }
      setEditingMd(null); setEditingVal("");
      setWhen("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore impostazione kickoff");
    } finally { setSaving(false); }
  };

  return (
    <div className="card-surface rounded-2xl p-5 md:p-6 space-y-4" data-testid="admin-kickoff-scheduler">
      <div className="flex items-start gap-3 flex-wrap">
        <div className="w-10 h-10 rounded-xl bg-amber-500 text-white flex items-center justify-center shrink-0">
          <CalendarDays className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-xs uppercase tracking-widest text-slate-500">Chiusura scommesse</div>
          <h2 className="display font-black text-2xl md:text-3xl mt-1">Kickoff giornata</h2>
          <p className="text-slate-600 text-sm mt-2 max-w-2xl">
            Imposta <b>data e ora della prima partita</b> di ogni giornata: da quell'istante
            i <b>mercati 1X2</b> di tutta la giornata verranno bloccati automaticamente per
            nuove puntate. Il <b>cashout</b> resta disponibile ancora 2 ore, poi si chiude.
          </p>
        </div>
      </div>

      {/* Form: imposta / correggi il kickoff di una giornata */}
      <div className="rounded-2xl border border-amber-200 bg-amber-50/40 p-4 flex flex-wrap items-end gap-3"
           data-testid="kickoff-form">
        <div>
          <Label className="text-slate-600 text-xs">Giornata</Label>
          <Input
            type="number"
            min={1}
            value={matchday}
            onChange={(e) => setMatchday(parseInt(e.target.value || "1", 10))}
            className="bg-white border-slate-200 mt-1 w-28"
            data-testid="kickoff-md-input"
          />
        </div>
        <div className="flex-1 min-w-[220px]">
          <Label className="text-slate-600 text-xs">Data e ora (prima partita)</Label>
          <Input
            type="datetime-local"
            value={when}
            onChange={(e) => setWhen(e.target.value)}
            className="bg-white border-slate-200 mt-1"
            data-testid="kickoff-datetime-input"
          />
          <div className="text-[11px] text-slate-500 mt-1 flex items-center gap-2 flex-wrap">
            <span>Fuso del tuo dispositivo (per l'Italia: Europe/Rome).</span>
            <button
              type="button"
              onClick={() => suggest(matchday)}
              disabled={suggesting}
              className="inline-flex items-center gap-1 rounded-full h-6 px-2 text-[10px] uppercase tracking-widest font-bold bg-indigo-100 hover:bg-indigo-200 text-indigo-700 disabled:opacity-50"
              data-testid="kickoff-suggest-btn"
              title="Prende la data/ora della prima partita di questa giornata dal feed Serie A"
            >
              <Sparkles className="w-3 h-3" />
              {suggesting ? "Cerco…" : "Suggerisci da Serie A"}
            </button>
          </div>
          {suggestion && (
            <div className="mt-2 rounded-lg bg-indigo-50 border border-indigo-200 px-2.5 py-1.5 text-[11px] text-indigo-900 flex items-center gap-1.5"
                 data-testid="kickoff-suggestion-preview">
              <Sparkles className="w-3 h-3 shrink-0" />
              <span>
                Serie A {suggestion.season} · G{suggestion.matchday}:
                <b className="ml-1">{suggestion.first_match.home}</b> vs <b>{suggestion.first_match.away}</b>
                {" · "}
                <span className="tabular-nums">{suggestion.first_match.date} {suggestion.first_match.time}</span>
                {" · "}
                <span className="text-indigo-700">{suggestion.match_count} partite</span>
              </span>
            </div>
          )}
        </div>
        <Button
          onClick={() => save(matchday, when)}
          disabled={saving || !when}
          className="btn-brand rounded-full"
          data-testid="kickoff-save-btn"
        >
          <Save className="w-4 h-4 mr-2" />
          {saving ? "Salvataggio…" : "Imposta kickoff"}
        </Button>
      </div>

      {/* Lista kickoff per giornata */}
      <div className="rounded-2xl border border-slate-200 overflow-hidden" data-testid="kickoff-table">
        <div className="grid grid-cols-12 bg-slate-50 text-[10px] uppercase tracking-widest font-bold text-slate-500 px-3 py-2">
          <div className="col-span-2 sm:col-span-1">G</div>
          <div className="col-span-5 sm:col-span-5">Kickoff</div>
          <div className="hidden sm:block sm:col-span-3">Stato</div>
          <div className="col-span-5 sm:col-span-3 text-right">Azioni</div>
        </div>
        {loading ? (
          <div className="p-6 text-center text-slate-500 text-sm">Caricamento…</div>
        ) : rows.length === 0 ? (
          <div className="p-6 text-center text-slate-500 text-sm">
            Nessun mercato 1X2 presente. Verranno creati automaticamente dopo la prima giornata caricata.
          </div>
        ) : rows.map((r) => {
          const isEditing = editingMd === r.matchday;
          return (
            <div key={r.matchday}
                 className="grid grid-cols-12 items-center px-3 py-2.5 border-t border-slate-100 gap-2"
                 data-testid={`kickoff-row-${r.matchday}`}>
              <div className="col-span-2 sm:col-span-1 font-black tabular-nums text-slate-900">G{r.matchday}</div>
              <div className="col-span-5 sm:col-span-5">
                {isEditing ? (
                  <Input
                    type="datetime-local"
                    value={editingVal}
                    onChange={(e) => setEditingVal(e.target.value)}
                    className="bg-white border-slate-200 h-8 text-xs"
                    data-testid={`kickoff-row-edit-${r.matchday}`}
                  />
                ) : (
                  <div className="text-sm">
                    <span className={r.kickoff_passed ? "text-slate-400 line-through" : "text-slate-800 font-semibold"}>
                      {_fmtKickoff(r.kickoff_at)}
                    </span>
                    {!r.kickoff_uniform && (
                      <span className="ml-2 text-[10px] uppercase tracking-widest font-bold text-amber-600">disomogeneo</span>
                    )}
                  </div>
                )}
              </div>
              <div className="hidden sm:flex sm:col-span-3 items-center gap-1 flex-wrap text-[11px]">
                {r.open > 0 && <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 h-5 px-2">{r.open} aperti</Badge>}
                {r.closed > 0 && <Badge className="bg-amber-100 text-amber-700 border-amber-200 h-5 px-2">{r.closed} chiusi</Badge>}
                {r.settled > 0 && <Badge className="bg-slate-100 text-slate-700 border-slate-200 h-5 px-2">{r.settled} liquidati</Badge>}
              </div>
              <div className="col-span-5 sm:col-span-3 flex items-center justify-end gap-1.5">
                {isEditing ? (
                  <>
                    <Button
                      size="sm"
                      className="btn-brand rounded-full h-8 text-xs"
                      disabled={saving || !editingVal}
                      onClick={() => save(r.matchday, editingVal)}
                      data-testid={`kickoff-row-save-${r.matchday}`}
                    >
                      <Save className="w-3.5 h-3.5 mr-1" />
                      Salva
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="rounded-full h-8 text-xs border-slate-300"
                      onClick={() => { setEditingMd(null); setEditingVal(""); }}
                    >
                      <X className="w-3.5 h-3.5" />
                    </Button>
                  </>
                ) : (
                  <Button
                    size="sm"
                    variant="outline"
                    className="rounded-full h-8 text-xs border-slate-300"
                    onClick={() => { setEditingMd(r.matchday); setEditingVal(_isoToLocal(r.kickoff_at)); }}
                    data-testid={`kickoff-row-edit-btn-${r.matchday}`}
                  >
                    <Pencil className="w-3.5 h-3.5 mr-1" />
                    Modifica
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function SimulateScoresPanel() {
  const [busy, setBusy] = useState(null);
  const [result, setResult] = useState(null);
  const simulate = async () => {
    if (!window.confirm("Genero punteggi casuali per tutte le 32 giornate? Le partite esistenti saranno sovrascritte.")) return;
    setBusy("sim"); setResult(null);
    try {
      const { data } = await api.post("/admin/dev/simulate-scores?matchdays=32");
      setResult(data);
      toast.success(`Generati ${data.matchdays_created} giornate · ${data.matches_created} partite`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore simulazione");
    } finally { setBusy(null); }
  };
  const reset = async () => {
    if (!window.confirm("Confermi l'azzeramento di TUTTI i risultati del campionato? Le rose e il calendario NON vengono toccati.")) return;
    setBusy("reset"); setResult(null);
    try {
      const { data } = await api.post("/admin/dev/reset-scores");
      setResult(data);
      toast.success(`Azzerati: ${data.matchdays_deleted} giornate, ${data.matches_deleted} partite, ${data.giornata_results_deleted} voti`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore azzeramento");
    } finally { setBusy(null); }
  };
  return (
    <div className="rounded-2xl border-2 border-violet-300 bg-violet-50 p-5" data-testid="simulate-scores-panel">
      <div className="flex items-start gap-3 flex-wrap">
        <Dice5 className="w-6 h-6 text-violet-700 shrink-0 mt-1" />
        <div className="flex-1 min-w-0">
          <div className="display font-black text-lg text-violet-900">Simulazione punteggi campionato</div>
          <p className="text-violet-900/80 text-sm mt-1">
            Genera in un click i punteggi casuali per tutte le <b>32 giornate</b> (usati per Classifica Generale e Classifica Gironi).
            Il pulsante di azzeramento pulisce tutti i risultati e riparte dal calendario senza partite giocate.
          </p>
        </div>
      </div>
      <div className="mt-4 flex gap-2 flex-wrap">
        <Button onClick={simulate} disabled={!!busy}
                className="bg-violet-600 hover:bg-violet-700 text-white rounded-full"
                data-testid="simulate-scores-btn">
          <Dice5 className="w-4 h-4 mr-1" />
          {busy === "sim" ? "Simulazione in corso…" : "🎲 Genera punteggi random (32 giornate)"}
        </Button>
        <Button onClick={reset} disabled={!!busy}
                variant="outline"
                className="border-red-300 text-red-700 hover:bg-red-50 rounded-full"
                data-testid="reset-scores-btn">
          <Trash2 className="w-4 h-4 mr-1" />
          {busy === "reset" ? "Azzeramento…" : "Azzera risultati campionato"}
        </Button>
      </div>
      {result && (
        <div className="mt-3 text-xs text-violet-900 bg-white/70 border border-violet-200 rounded-lg p-3" data-testid="simulate-result">
          {result.matchdays_created != null ? (
            <>✔ Simulazione completata: <b>{result.matchdays_created}</b> giornate · <b>{result.matches_created}</b> partite · <b>{result.users}</b> utenti scored</>
          ) : (
            <>✔ Azzeramento: <b>{result.matchdays_deleted}</b> giornate · <b>{result.matches_deleted}</b> partite · <b>{result.giornata_results_deleted}</b> voti giocatori</>
          )}
        </div>
      )}
    </div>
  );
}

function MatchdayUploadPanel() {
  const [matchday, setMatchday] = useState(1);
  const [status, setStatus] = useState({});
  const [busy, setBusy] = useState(null);
  // bulkProgress: [{name, girone, state, phase, msg, startedAt, endedAt}]
  // states: 'queued' | 'uploading' | 'analyzing' | 'ok' | 'error'
  const [bulkProgress, setBulkProgress] = useState([]);
  const [bulkLabel, setBulkLabel] = useState(""); // "Girone A" / "Giornata intera"
  const [bulkStartedAt, setBulkStartedAt] = useState(null);

  const load = async () => {
    try { const { data } = await api.get("/admin/giornata/status"); setStatus(data || {}); } catch {}
  };
  useEffect(() => { load(); }, []);

  // Timeout esteso per singola immagine LLM (fino a 5 minuti). Non impostare
  // timeout=0 su axios: alcune build lo interpretano come "abortisci subito".
  const UPLOAD_TIMEOUT_MS = 5 * 60 * 1000;

  const uploadOne = async (g, slotIdx, file) => {
    if (!file) return;
    if (!/\.(xlsx|jpg|jpeg|png|webp)$/i.test(file.name)) return toast.error("Serve una foto JPG/PNG o un file .xlsx");
    setBusy(`${matchday}-${g}-${slotIdx}`);
    const fd = new FormData();
    fd.append("girone", g);
    fd.append("matchday", String(matchday));
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/giornata/upload", fd, { timeout: UPLOAD_TIMEOUT_MS });
      toast.success(`G${data.matchday} · ${g}: ${data.matches_resolved}/${data.matches} match · ${data.ratings} voti`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload fallito");
    } finally { setBusy(null); }
  };

  /**
   * Bulk upload con concorrenza limitata (4 in parallelo) e progress a 3 fasi:
   * 1) `uploading`: bytes → server (progressioe axios reale)
   * 2) `analyzing`: server → Gemini Vision (stimato ~15-20s, animato)
   * 3) `ok`/`error`: risultato
   *
   * Il browser limita a ~6 connessioni parallele per origine e Gemini ha
   * rate-limit lato provider, quindi non abbiamo alcun vantaggio a lanciare
   * 16 richieste tutte insieme: la fila si crea da sola e il tempo totale
   * cresce. Con concurrency=4 chiudiamo 4 partite ogni ~20s → 16 in ~80s.
   */
  const uploadMany = async (files, girone) => {
    if (!files || files.length === 0) return;
    const valid = Array.from(files).filter((f) => /\.(jpg|jpeg|png|webp)$/i.test(f.name));
    if (valid.length === 0) return toast.error("Nessuna immagine valida (JPG/PNG/WEBP)");
    if (valid.length !== files.length) {
      toast.warning(`${files.length - valid.length} file scartati (formato non supportato)`);
    }
    const label = girone === "AUTO" ? "Giornata intera" : `Girone ${girone}`;
    setBulkLabel(label);
    setBulkStartedAt(Date.now());
    setBusy(`bulk-${girone}`);
    setBulkProgress(valid.map((f) => ({
      name: f.name, girone, state: "queued", phase: 0, msg: "", uploadPct: 0,
    })));

    // Richiedi permesso notifiche browser (non blocking)
    try {
      if ("Notification" in window && Notification.permission === "default") {
        Notification.requestPermission().catch(() => {});
      }
    } catch { /* ignore */ }

    const CONCURRENCY = 4;
    let cursor = 0;
    const results = new Array(valid.length);

    const runOne = async (idx) => {
      const file = valid[idx];
      const startedAt = Date.now();
      setBulkProgress((prev) => prev.map((p, i) => i === idx ? {
        ...p, state: "uploading", phase: 1, uploadPct: 0, startedAt,
      } : p));
      const fd = new FormData();
      fd.append("girone", girone);
      fd.append("matchday", String(matchday));
      fd.append("file", file);
      try {
        const { data } = await api.post("/admin/giornata/upload", fd, {
          timeout: UPLOAD_TIMEOUT_MS,
          onUploadProgress: (ev) => {
            const total = ev.total || file.size || 1;
            const pct = Math.min(99, Math.round((ev.loaded / total) * 100));
            setBulkProgress((prev) => prev.map((p, i) => i === idx ? {
              ...p,
              state: pct >= 99 ? "analyzing" : "uploading",
              phase: pct >= 99 ? 2 : 1,
              uploadPct: pct,
            } : p));
          },
        });
        // Alcuni browser skippano l'ultima chiamata onUploadProgress: se
        // arriviamo qui senza aver visto lo state="analyzing" lo forziamo
        // brevemente per completezza visiva. Poi passiamo a "ok".
        setBulkProgress((prev) => prev.map((p, i) => i === idx ? {
          ...p, state: "ok", phase: 3, uploadPct: 100,
          endedAt: Date.now(),
          girone: data.girone || p.girone,
          msg: `${data.girone}: ${data.matches_resolved}/${data.matches} match · ${data.ratings} voti`,
        } : p));
        return { ok: true };
      } catch (e) {
        const msg = e?.response?.data?.detail || e?.message || "Errore upload";
        setBulkProgress((prev) => prev.map((p, i) => i === idx ? {
          ...p, state: "error", phase: 3, endedAt: Date.now(), msg,
        } : p));
        return { ok: false, msg };
      }
    };

    // Worker loop: 4 worker in parallelo che pescano dalla coda
    const worker = async () => {
      while (true) {
        const idx = cursor++;
        if (idx >= valid.length) return;
        results[idx] = await runOne(idx);
      }
    };
    await Promise.all(Array.from({ length: Math.min(CONCURRENCY, valid.length) }, worker));

    const ok = results.filter((r) => r?.ok).length;
    const ko = results.length - ok;
    await load();
    setBusy(null);
    const elapsed = Math.round((Date.now() - (bulkStartedAt || Date.now())) / 1000);
    // Notifica browser + toast
    const summary = `${ok}/${results.length} partite analizzate in ${elapsed}s${ko ? ` · ${ko} errori` : ""}`;
    try {
      if ("Notification" in window && Notification.permission === "granted") {
        new Notification(`FIFE · ${label} caricata`, { body: summary, icon: "/icon-192.png" });
      }
    } catch { /* ignore */ }
    if (ko === 0) toast.success(`${label}: ${summary}`);
    else toast.warning(`${label}: ${summary} — controlla i dettagli`);
  };

  const retryOne = async (idx) => {
    // Ripete solo la voce fallita — il file non è più in state, quindi mostriamo msg
    toast.info("Riseleziona il file per riprovare: non posso ripescarlo dal browser");
  };

  const clearBulkProgress = () => setBulkProgress([]);

  const perGiornata = status[String(matchday)] || {};
  const totalDone = Object.values(perGiornata).reduce((a, arr) => a + (arr?.length || 0), 0);

  const runDedupe = async () => {
    if (!window.confirm("Rimuovere i duplicati di campionato_matches e giornata_results? Preserva sempre l'ultima versione di ogni partita.")) return;
    setBusy("dedupe");
    try {
      const { data } = await api.post("/admin/giornata/dedupe");
      toast.success(`Duplicati rimossi: ${data.campionato_matches_removed} match · ${data.giornata_results_removed} risultati`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Dedup fallito");
    } finally { setBusy(null); }
  };

  const runDeleteMatchday = async () => {
    if (!window.confirm(
      `⚠️ Confermi l'eliminazione TOTALE dei risultati della Giornata ${matchday}?\n\n` +
      `Verranno rimossi:\n` +
      `• Risultati parsati (giornata_results)\n` +
      `• Partite head-to-head (campionato_matches)\n` +
      `• Voti e eventi dei calciatori (player_ratings)\n` +
      `• Score della giornata (dalla classifica)\n\n` +
      `Le altre giornate, l'asta, gli utenti e le scommesse restano intatte. ` +
      `La classifica si ricalcola automaticamente. Continuare?`
    )) return;
    setBusy("delete-md");
    try {
      const { data } = await api.delete(`/admin/giornata/${matchday}`);
      toast.success(
        `G${data.matchday} eliminata · ${data.campionato_matches_deleted} match · ` +
        `${data.giornata_results_deleted} risultati · ${data.player_ratings_deleted} voti · ` +
        `${data.matchdays_deleted} score`
      );
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eliminazione fallita");
    } finally { setBusy(null); }
  };

  return (
    <div className="card-surface rounded-2xl p-5 md:p-6 space-y-4" data-testid="admin-giornata-upload">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500">Risultati di giornata</div>
          <h2 className="display font-black text-2xl md:text-3xl mt-1">Upload per Partita</h2>
          <p className="text-slate-600 text-sm mt-2 max-w-2xl">
            Carica gli <b>screenshot</b> (JPG/PNG) delle partite. Puoi caricare tutto in un colpo solo:
            <b> 4 immagini per girone</b> (bottone del girone) oppure <b>tutti i 16 file della giornata</b> (bottone in alto).
            Le foto vengono analizzate <b>in parallelo</b> dall'AI: modulo, punteggio, fantavoto totale, voti + eventi di ogni calciatore (titolari e panchina).
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={runDeleteMatchday}
            className="rounded-full h-9 border-red-300 text-red-700 hover:bg-red-50 text-xs"
            disabled={busy === "delete-md"}
            data-testid="giornata-delete-btn"
            title={`Elimina TUTTI i risultati della Giornata ${matchday} (classifica, voti, partite)`}
          >
            <Trash2 className="w-3.5 h-3.5 mr-1" />
            {busy === "delete-md" ? "Elimino…" : `Elimina G${matchday}`}
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={runDedupe}
            className="rounded-full h-9 border-slate-300 text-xs"
            disabled={busy === "dedupe"}
            data-testid="giornata-dedupe-btn"
            title="Rimuove i duplicati di partite/risultati (case-insensitive)"
          >
            <Trash2 className="w-3.5 h-3.5 mr-1" />
            {busy === "dedupe" ? "Pulizia…" : "Rimuovi duplicati"}
          </Button>
          <div>
            <Label className="text-slate-600 text-xs">Giornata</Label>
            <Input type="number" min={1} value={matchday}
                   onChange={(e) => setMatchday(parseInt(e.target.value || "1", 10))}
                   className="bg-slate-50 border-slate-200 mt-1 w-28" data-testid="giornata-number-upload" />
          </div>
        </div>
      </div>

      {/* Bulk upload 16 partite intera giornata (auto-detect girone) */}
      <div className="rounded-2xl border border-indigo-200 bg-indigo-50/40 p-4 flex flex-wrap items-center justify-between gap-3"
           data-testid="giornata-bulk-all">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 rounded-xl bg-indigo-500 text-white flex items-center justify-center">
            <Upload className="w-5 h-5" />
          </div>
          <div className="min-w-0">
            <div className="display font-bold text-lg">Carica intera giornata (16 partite)</div>
            <div className="text-xs text-slate-600">
              Seleziona fino a 16 screenshot: l'AI rileva automaticamente il girone di ogni partita. Analisi in parallelo, ~20-30s totali.
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="text-sm text-slate-700 tabular-nums font-bold">{totalDone}/16</div>
          <input
            id="gio-file-bulk-all"
            type="file"
            multiple
            accept="image/jpeg,image/png,image/webp,.jpg,.jpeg,.png,.webp"
            className="hidden"
            onChange={(e) => { const fs = e.target.files; e.target.value = ""; uploadMany(fs, "AUTO"); }}
            data-testid="giornata-file-bulk-all"
          />
          <Button
            type="button"
            onClick={() => document.getElementById("gio-file-bulk-all")?.click()}
            className="btn-brand rounded-full"
            disabled={busy?.startsWith("bulk-")}
            data-testid="giornata-upload-bulk-all"
          >
            <Upload className="w-4 h-4 mr-2" />
            {busy === "bulk-AUTO" ? "Analizzo…" : "Seleziona 16 foto"}
          </Button>
        </div>
      </div>

      {/* Progress bulk creativo — sticky overlay quando c'è un'analisi in corso */}
      {bulkProgress.length > 0 && (() => {
        const total = bulkProgress.length;
        const done = bulkProgress.filter((p) => p.state === "ok").length;
        const errors = bulkProgress.filter((p) => p.state === "error").length;
        const uploading = bulkProgress.filter((p) => p.state === "uploading").length;
        const analyzing = bulkProgress.filter((p) => p.state === "analyzing").length;
        const inProgress = uploading + analyzing;
        const donePct = Math.round((done + errors) / total * 100);
        const isRunning = inProgress > 0;
        const elapsed = bulkStartedAt ? Math.round((Date.now() - bulkStartedAt) / 1000) : 0;
        return (
          <div className="rounded-2xl border-2 border-indigo-200 bg-gradient-to-br from-indigo-50 via-white to-purple-50 p-4 space-y-3 relative overflow-hidden"
               data-testid="giornata-bulk-progress">
            {/* Sfondo animato quando in corso */}
            {isRunning && (
              <div className="absolute inset-0 opacity-30 pointer-events-none">
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-indigo-200 to-transparent animate-[shimmer_2s_infinite]" style={{ backgroundSize: "200% 100%" }} />
              </div>
            )}
            <div className="relative flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isRunning ? "bg-indigo-500 text-white animate-pulse" : done === total ? "bg-emerald-500 text-white" : "bg-slate-300 text-white"}`}>
                  {isRunning ? <Sparkles className="w-5 h-5" /> : done === total ? <CheckCircle2 className="w-5 h-5" /> : <BellRing className="w-5 h-5" />}
                </div>
                <div>
                  <div className="display font-black text-lg">
                    {isRunning ? `Analisi ${bulkLabel} in corso…` : done === total ? `${bulkLabel} caricata!` : `${bulkLabel} · in pausa`}
                  </div>
                  <div className="text-xs text-slate-600">
                    <b className="tabular-nums">{done}</b>/{total} completate
                    {errors > 0 && <span className="text-red-600"> · {errors} errori</span>}
                    {isRunning && <span className="text-indigo-600"> · {inProgress} in lavorazione</span>}
                    <span className="text-slate-400"> · {elapsed}s</span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <div className="text-2xl font-black tabular-nums" style={{ color: donePct === 100 ? "#059669" : "#4f46e5" }}>{donePct}%</div>
                {!isRunning && (
                  <button type="button" onClick={clearBulkProgress}
                          className="rounded-full h-8 px-3 text-[10px] uppercase tracking-widest font-bold bg-slate-100 hover:bg-slate-200 text-slate-600"
                          data-testid="giornata-bulk-close">
                    Chiudi
                  </button>
                )}
              </div>
            </div>

            {/* Barra di progresso principale (creativa: 3 segmenti colorati animati) */}
            <div className="relative h-3 rounded-full bg-slate-200 overflow-hidden">
              <div className="absolute inset-y-0 left-0 bg-emerald-500 transition-all duration-500"
                   style={{ width: `${(done / total) * 100}%` }} />
              <div className="absolute inset-y-0 bg-red-500 transition-all duration-500"
                   style={{ left: `${(done / total) * 100}%`, width: `${(errors / total) * 100}%` }} />
              <div className="absolute inset-y-0 bg-indigo-500 animate-pulse transition-all duration-500"
                   style={{ left: `${((done + errors) / total) * 100}%`, width: `${(inProgress / total) * 100}%` }} />
            </div>

            {/* Lista file con card animate */}
            <div className="relative grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-96 overflow-y-auto">
              {bulkProgress.map((p, i) => {
                const isDone = p.state === "ok";
                const isErr = p.state === "error";
                const isRun = p.state === "uploading" || p.state === "analyzing";
                const color = isDone ? "emerald" : isErr ? "red" : "indigo";
                return (
                  <div key={i}
                       className={`rounded-xl border bg-white p-2.5 flex items-center gap-2 transition-all ${isRun ? "border-indigo-300 shadow-sm" : isDone ? "border-emerald-200" : isErr ? "border-red-200" : "border-slate-200"}`}
                       data-testid={`bulk-item-${i}`}>
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 bg-${color}-100 text-${color}-600`}>
                      {p.state === "queued" && <span className="text-[10px] font-bold">…</span>}
                      {p.state === "uploading" && <Upload className="w-4 h-4 animate-bounce" />}
                      {p.state === "analyzing" && <Sparkles className="w-4 h-4 animate-spin" style={{ animationDuration: "3s" }} />}
                      {isDone && <CheckCircle2 className="w-4 h-4" />}
                      {isErr && <X className="w-4 h-4" />}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-[11px] font-bold text-slate-800 truncate">{p.name}</div>
                      <div className="text-[10px] text-slate-500 truncate">
                        {p.state === "queued" && "in coda…"}
                        {p.state === "uploading" && `caricamento ${p.uploadPct || 0}%`}
                        {p.state === "analyzing" && "AI Vision sta leggendo…"}
                        {isDone && p.msg}
                        {isErr && <span className="text-red-600">{p.msg}</span>}
                      </div>
                      {isRun && (
                        <div className="mt-1 h-1 rounded-full bg-slate-100 overflow-hidden">
                          <div className={`h-full bg-${color}-500 transition-all`}
                               style={{
                                 width: p.state === "uploading" ? `${p.uploadPct || 0}%` : "70%",
                                 animation: p.state === "analyzing" ? "pulse 2s infinite" : undefined,
                               }} />
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {GIRONI.map((g) => {
          const matches = perGiornata[g] || [];
          const gDone = matches.length;
          const bulkKey = `gio-file-bulk-${g}`;
          return (
            <div key={g} className={`rounded-2xl border p-4 ${gDone >= 4 ? "border-emerald-200 bg-emerald-50/40" : "border-slate-200"}`}
                 data-testid={`giornata-card-${g}`}>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-3">
                  <GironeBadge girone={g} size={36} />
                  <div>
                    <div className="display font-bold text-lg">Girone {g}</div>
                    <div className="text-xs text-slate-500">
                      {gDone === 0 ? "Nessun match caricato" : `${gDone}/4 match caricati`}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {gDone >= 4 && <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200"><CheckCircle2 className="w-3.5 h-3.5 mr-1"/>Completo</Badge>}
                  <input
                    id={bulkKey}
                    type="file"
                    multiple
                    accept="image/jpeg,image/png,image/webp,.jpg,.jpeg,.png,.webp"
                    className="hidden"
                    onChange={(e) => { const fs = e.target.files; e.target.value = ""; uploadMany(fs, g); }}
                    data-testid={`giornata-file-bulk-${g}`}
                  />
                  <Button
                    type="button"
                    onClick={() => document.getElementById(bulkKey)?.click()}
                    className="btn-brand rounded-full h-8 text-xs"
                    disabled={busy === `bulk-${g}`}
                    data-testid={`giornata-upload-bulk-${g}`}
                  >
                    <Upload className="w-3.5 h-3.5 mr-1" />
                    {busy === `bulk-${g}` ? "Analizzo…" : "Carica 4 foto"}
                  </Button>
                </div>
              </div>

              <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
                {[0, 1, 2, 3].map((slot) => {
                  const m = matches[slot];
                  const filled = !!m;
                  const key = `${matchday}-${g}-${slot}`;
                  const inputId = `gio-file-${g}-${slot}`;
                  return (
                    <div key={slot}
                         className={`rounded-xl border p-3 flex flex-col gap-2 min-h-[92px] ${filled ? "border-emerald-200 bg-white" : "border-dashed border-slate-300 bg-slate-50/50"}`}
                         data-testid={`giornata-slot-${g}-${slot}`}>
                      <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 flex items-center justify-between">
                        <span>Partita {slot + 1}</span>
                        {filled && <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />}
                      </div>
                      {filled ? (
                        <div className="flex-1 min-w-0">
                          <div className="text-xs font-bold text-slate-900 truncate">
                            {m.home_team} <span className="text-slate-400">vs</span> {m.away_team}
                          </div>
                          <div className="mt-0.5 tabular-nums text-sm text-slate-700">
                            <b>{m.home_score ?? "-"} – {m.away_score ?? "-"}</b>
                            <span className="text-slate-400 text-[10px] ml-1">
                              ({m.home_totale?.toFixed?.(1) ?? "-"}–{m.away_totale?.toFixed?.(1) ?? "-"})
                            </span>
                          </div>
                        </div>
                      ) : (
                        <div className="text-xs text-slate-400 italic flex-1">In attesa del file</div>
                      )}
                      <input
                        id={inputId}
                        type="file"
                        accept="image/jpeg,image/png,image/webp,.jpg,.jpeg,.png,.webp"
                        className="hidden"
                        onChange={(e) => uploadOne(g, slot, e.target.files?.[0])}
                        data-testid={`giornata-file-${g}-${slot}`}
                      />
                      <Button
                        type="button"
                        onClick={() => document.getElementById(inputId)?.click()}
                        variant={filled ? "outline" : "default"}
                        className={filled ? "rounded-full h-8 text-xs border-slate-300" : "btn-brand rounded-full h-8 text-xs"}
                        disabled={busy === key}
                        data-testid={`giornata-upload-${g}-${slot}`}
                      >
                        <Upload className="w-3.5 h-3.5 mr-1" />
                        {busy === key ? "Analizzo…" : filled ? "Sostituisci" : `Foto ${slot + 1}`}
                      </Button>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function MatchdayManualPanel() {
  const [teams, setTeams] = useState([]);
  const [matchdays, setMatchdays] = useState([]);
  const [number, setNumber] = useState(1);
  const [scores, setScores] = useState({}); // user_id -> string
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [t, md] = await Promise.all([
      api.get("/public/standings"),
      api.get("/public/matchdays"),
    ]);
    setTeams(t.data); setMatchdays(md.data);
  };
  useEffect(() => { load(); }, []);

  // Preload scores when number changes
  useEffect(() => {
    const md = matchdays.find((m) => m.number === number);
    const map = {};
    (md?.scores || []).forEach((s) => { map[s.user_id] = String(s.points); });
    setScores(map);
  }, [number, matchdays]);

  const submit = async () => {
    setBusy(true);
    try {
      const arr = teams.map((t) => ({ user_id: t.user_id, points: parseFloat(scores[t.user_id] || "0") }));
      await api.post("/admin/matchdays", { number, scores: arr });
      toast.success(`Giornata ${number} salvata`);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore salvataggio"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4">
      <div className="card-surface rounded-2xl p-5 flex items-center gap-3 flex-wrap">
        <Trophy className="w-5 h-5" style={{ color: "var(--brand)" }} />
        <div className="flex-1 min-w-[200px]">
          <div className="display font-bold text-lg">Override manuale punti giornata</div>
          <p className="text-slate-500 text-sm">Utile per correggere singoli punteggi senza ricaricare l'Excel. La classifica si aggiorna automaticamente.</p>
        </div>
        <div>
          <Label className="text-slate-600 text-xs">Giornata</Label>
          <Input type="number" min={1} value={number} onChange={(e) => setNumber(parseInt(e.target.value || "1", 10))}
                 className="bg-slate-50 border-slate-200 mt-1 w-24" data-testid="matchday-number" />
        </div>
        <Button onClick={submit} disabled={busy} className="btn-brand rounded-full" data-testid="matchday-save">
          <Save className="w-4 h-4 mr-2" /> {busy ? "Salvataggio..." : "Salva giornata"}
        </Button>
      </div>
      {teams.length === 0 ? (
        <div className="card-surface rounded-2xl p-8 text-center text-slate-500">Nessuna squadra approvata ancora.</div>
      ) : (
        <div className="card-surface rounded-2xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 uppercase text-[10px] tracking-widest">
              <tr>
                <th className="px-3 py-3 text-left">Squadra</th>
                <th className="px-3 py-3 text-center">Girone</th>
                <th className="px-3 py-3 text-center">Tot Pt</th>
                <th className="px-3 py-3 text-right w-32">Punti G{number}</th>
              </tr>
            </thead>
            <tbody>
              {teams.map((t) => (
                <tr key={t.user_id} className="border-t border-slate-100">
                  <td className="px-3 py-3">
                    <div className="flex items-center gap-2">
                      <img src={t.shield_path ? publicFileUrl(t.shield_path) : ""} alt="" className="w-6 h-6 rounded object-cover bg-slate-50" />
                      <span>{t.team_name}</span>
                    </div>
                  </td>
                  <td className="px-3 py-3 text-center text-slate-500">{t.girone}</td>
                  <td className="px-3 py-3 text-center font-black" style={{ color: "var(--brand)" }}>{t.points.toFixed(1)}</td>
                  <td className="px-3 py-3 text-right">
                    <Input type="number" step="0.5" value={scores[t.user_id] || ""}
                           onChange={(e) => setScores((s) => ({ ...s, [t.user_id]: e.target.value }))}
                           className="bg-slate-50 border-slate-200 text-right w-28 ml-auto"
                           data-testid={`score-${t.user_id}`} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ============ INTERVISTE TAB (Admin CRUD) ============ */
function IntervisteTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // {id, tone, headline, quote}
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/interviews");
      setItems(data);
    } catch (e) { toast.error("Errore caricamento interviste"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const saveEdit = async () => {
    if (!editing) return;
    if (editing.headline.trim().length < 3) return toast.error("Titolo troppo corto");
    if (editing.quote.trim().length < 5) return toast.error("Testo troppo corto");
    setBusy(true);
    try {
      await api.patch(`/admin/interviews/${editing.id}`, {
        tone: editing.tone,
        headline: editing.headline.trim(),
        quote: editing.quote.trim(),
      });
      toast.success("Intervista aggiornata");
      setEditing(null);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore salvataggio"); }
    finally { setBusy(false); }
  };

  const remove = async (it) => {
    if (!window.confirm(`Eliminare definitivamente «${it.headline}»?`)) return;
    setBusy(true);
    try {
      await api.delete(`/admin/interviews/${it.id}`);
      toast.success("Intervista eliminata");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore eliminazione"); }
    finally { setBusy(false); }
  };

  const filtered = items.filter((it) => {
    const q = filter.trim().toLowerCase();
    if (!q) return true;
    return (it.headline || "").toLowerCase().includes(q)
        || (it.team_name || "").toLowerCase().includes(q)
        || (it.coach_name || "").toLowerCase().includes(q);
  });

  return (
    <div className="space-y-4">
      <div className="card-surface rounded-2xl p-5 flex items-center gap-3 flex-wrap">
        <div className="flex-1 min-w-[200px]">
          <div className="display font-bold text-lg">Interviste pubblicate</div>
          <p className="text-slate-500 text-sm">Modifica il testo o elimina definitivamente le conferenze stampa dei fantallenatori.</p>
        </div>
        <Input value={filter} onChange={(e) => setFilter(e.target.value)}
               placeholder="Cerca squadra, allenatore, titolo…"
               className="bg-slate-50 border-slate-200 max-w-xs" data-testid="admin-interviste-search" />
      </div>

      {loading ? (
        <div className="text-center text-slate-500 py-8">Caricamento…</div>
      ) : filtered.length === 0 ? (
        <div className="card-surface rounded-2xl p-10 text-center text-slate-500">Nessuna intervista trovata.</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 auto-rows-fr">
          {filtered.map((it) => (
            <div key={it.id} className="card-surface rounded-2xl p-4 flex flex-col h-full" data-testid={`admin-interview-${it.id}`}>
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-slate-500">
                <span className="px-1.5 py-0.5 rounded-full font-black" style={{ background: "#3b56e022", color: "#3b56e0" }}>{it.tone}</span>
                <span>{it.team_name} · Girone {it.girone}</span>
                {it.matchday && <span className="ml-auto">G{it.matchday}</span>}
              </div>
              <h4 className="display font-black text-base mt-1 line-clamp-2">«{it.headline}»</h4>
              <p className="text-slate-600 text-sm mt-1 italic line-clamp-3 flex-1">{it.quote}</p>
              <div className="text-xs text-slate-500 mt-2">— {it.coach_name || "—"} · {new Date(it.created_at).toLocaleString("it-IT")}</div>
              <div className="flex gap-2 mt-3">
                <Button size="sm" variant="outline" onClick={() => setEditing({ id: it.id, tone: it.tone, headline: it.headline, quote: it.quote })}
                        className="rounded-full border-slate-300 flex-1" data-testid={`admin-interview-edit-${it.id}`}>
                  <Pencil className="w-3.5 h-3.5 mr-1"/> Modifica
                </Button>
                <Button size="sm" onClick={() => remove(it)} disabled={busy}
                        className="rounded-full bg-red-600 hover:bg-red-700 text-white flex-1" data-testid={`admin-interview-delete-${it.id}`}>
                  <Trash2 className="w-3.5 h-3.5 mr-1"/> Elimina
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="bg-white border-slate-200 text-slate-900">
          <DialogHeader>
            <DialogTitle className="display font-black text-2xl">Modifica intervista</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div>
                <Label className="text-slate-600 text-xs">Tono</Label>
                <Select value={editing.tone} onValueChange={(v) => setEditing((e) => ({ ...e, tone: v }))}>
                  <SelectTrigger className="bg-slate-50 border-slate-200 mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="dichiarazione">📣 Dichiarazione</SelectItem>
                    <SelectItem value="fiducia">💪 Fiducia</SelectItem>
                    <SelectItem value="rabbia">🔥 Rabbia</SelectItem>
                    <SelectItem value="ironia">😏 Ironia</SelectItem>
                    <SelectItem value="disperazione">😭 Disperazione</SelectItem>
                    <SelectItem value="resa">🏳️ Resa</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-slate-600 text-xs">Titolo</Label>
                <Input value={editing.headline} maxLength={100}
                       onChange={(e) => setEditing((s) => ({ ...s, headline: e.target.value }))}
                       className="bg-slate-50 border-slate-200 mt-1" data-testid="admin-edit-headline" />
                <div className="text-[10px] text-slate-400 text-right mt-1">{editing.headline.length}/100</div>
              </div>
              <div>
                <Label className="text-slate-600 text-xs">Testo</Label>
                <Textarea value={editing.quote} maxLength={700}
                          onChange={(e) => setEditing((s) => ({ ...s, quote: e.target.value }))}
                          className="bg-slate-50 border-slate-200 mt-1 min-h-[140px]" data-testid="admin-edit-quote" />
                <div className="text-[10px] text-slate-400 text-right mt-1">{editing.quote.length}/700</div>
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setEditing(null)} className="border-slate-300 rounded-full">Annulla</Button>
                <Button onClick={saveEdit} disabled={busy} className="btn-brand rounded-full" data-testid="admin-edit-save">
                  {busy ? "Salvataggio…" : "Salva"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ============ MARKETS TAB ============ */
function MarketsTab() {
  const [markets, setMarkets] = useState([]);
  const [teams, setTeams] = useState([]);
  const [busy, setBusy] = useState(false);
  const [title, setTitle] = useState("");
  const [matchday, setMatchday] = useState(1);
  const [options, setOptions] = useState([{ label: "", odds: 2.0 }, { label: "", odds: 2.0 }]);
  const [autoHome, setAutoHome] = useState("");
  const [autoAway, setAutoAway] = useState("");
  const [autoMd, setAutoMd] = useState(1);

  const load = async () => {
    const [m, t] = await Promise.all([api.get("/public/markets"), api.get("/public/standings")]);
    setMarkets(m.data); setTeams(t.data);
  };
  useEffect(() => { load(); }, []);

  const addOption = () => setOptions((o) => [...o, { label: "", odds: 2.0 }]);
  const removeOption = (i) => setOptions((o) => o.filter((_, idx) => idx !== i));

  const create = async () => {
    if (!title.trim() || options.some((o) => !o.label.trim())) return toast.error("Compila titolo e tutte le opzioni");
    setBusy(true);
    try {
      await api.post("/admin/markets", {
        matchday: parseInt(matchday, 10),
        title,
        options: options.map((o) => ({ label: o.label, odds: parseFloat(o.odds) })),
      });
      toast.success("Mercato creato");
      setTitle(""); setOptions([{ label: "", odds: 2.0 }, { label: "", odds: 2.0 }]);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore creazione"); }
    finally { setBusy(false); }
  };

  const createAuto = async () => {
    if (!autoHome || !autoAway) return toast.error("Scegli due squadre");
    if (autoHome === autoAway) return toast.error("Le squadre devono essere diverse");
    setBusy(true);
    try {
      const { data } = await api.post("/admin/markets/auto", {
        matchday: parseInt(autoMd, 10),
        home_user_id: autoHome,
        away_user_id: autoAway,
      });
      toast.success(`Mercato auto creato: ${data.options.map((o) => `x${o.odds}`).join(" · ")}`);
      setAutoHome(""); setAutoAway("");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore creazione"); }
    finally { setBusy(false); }
  };

  const [nextBusy, setNextBusy] = useState(false);
  const [nextResult, setNextResult] = useState(null);
  const createNextMatchday = async () => {
    setNextBusy(true);
    setNextResult(null);
    try {
      const { data } = await api.post("/admin/markets/next-matchday", {});
      setNextResult(data);
      toast.success(`Giornata ${data.next_matchday}: creati ${data.created} mercati (saltati ${data.skipped})`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore creazione prossima giornata");
    } finally { setNextBusy(false); }
  };

  const closeMarket = async (id) => {
    try { await api.post(`/admin/markets/${id}/close`); toast.success("Mercato chiuso"); load(); }
    catch { toast.error("Errore"); }
  };
  const settleMarket = async (market, optId) => {
    if (!window.confirm(`Liquidare "${market.title}" con opzione vincente selezionata?`)) return;
    try {
      await api.post(`/admin/markets/${market.id}/settle`, { winning_option_id: optId });
      toast.success("Mercato liquidato: puntate pagate e notifiche inviate.");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  return (
    <div className="space-y-4">
      {/* Next Matchday auto-batch */}
      <div className="rounded-2xl border-2 border-violet-300 bg-violet-50 p-5">
        <div className="flex items-start gap-3">
          <Sparkles className="w-6 h-6 text-violet-700 shrink-0 mt-1" />
          <div className="flex-1">
            <div className="display font-black text-lg text-violet-900">Prossima giornata — mercati automatici</div>
            <p className="text-violet-900/80 text-sm mt-1">
              Genera in un click tutti i mercati <b>1·X·2</b> per la prossima giornata FIFE. Prende gli abbinamenti dal
              <b> Calendario</b> caricato in Admin e calcola le quote con l'algoritmo già in uso (valore rosa + forma recente).
              Idempotente: le partite già presenti vengono saltate.
            </p>
            <div className="mt-3">
              <Button onClick={createNextMatchday} disabled={nextBusy}
                      className="bg-violet-600 hover:bg-violet-700 text-white rounded-full"
                      data-testid="admin-next-matchday-btn">
                {nextBusy ? "Generazione…" : "🎲 Genera mercati Prossima Giornata"}
              </Button>
            </div>
            {nextResult && (
              <div className="mt-3 text-sm text-violet-900" data-testid="next-matchday-result">
                <div>Giornata <b>{nextResult.next_matchday}</b> · creati <b>{nextResult.created}</b> · saltati <b>{nextResult.skipped}</b>
                  {nextResult.errors?.length > 0 && <> · errori <b>{nextResult.errors.length}</b></>}
                </div>
                {nextResult.markets?.length > 0 && (
                  <ul className="mt-2 space-y-1 text-xs">
                    {nextResult.markets.map((m, i) => (
                      <li key={i}>
                        <b>[{m.girone}]</b> {m.title} — <span className="text-violet-700">
                          1: x{m.odds.home} · X: x{m.odds.draw} · 2: x{m.odds.away}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
                {nextResult.errors?.length > 0 && (
                  <ul className="mt-2 space-y-1 text-xs text-red-700">
                    {nextResult.errors.map((e, i) => (
                      <li key={i}>⚠️ {e.match} ({e.girone}): {e.reason}</li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Auto market */}
      <div className="card-surface rounded-2xl p-5 space-y-3">
        <div className="flex items-center gap-2">
          <Sparkles className="w-5 h-5" style={{ color: "var(--brand)" }} />
          <div className="display font-bold text-lg">Quote automatiche (1·X·2)</div>
        </div>
        <p className="text-slate-500 text-sm">Genera un mercato per una sfida tra due squadre. Le quote si calcolano dalla forma media in campionato.</p>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div className="md:col-span-1">
            <Label className="text-slate-600 text-xs">Giornata</Label>
            <Input type="number" min={1} value={autoMd} onChange={(e) => setAutoMd(e.target.value)}
                   className="bg-slate-50 border-slate-200 mt-1" data-testid="auto-matchday" />
          </div>
          <div>
            <Label className="text-slate-600 text-xs">Casa</Label>
            <Select value={autoHome} onValueChange={setAutoHome}>
              <SelectTrigger className="bg-slate-50 border-slate-200 mt-1" data-testid="auto-home"><SelectValue placeholder="Squadra" /></SelectTrigger>
              <SelectContent>
                {teams.map((t) => <SelectItem key={t.user_id} value={t.user_id}>{t.team_name} · {t.girone} ({t.points.toFixed(1)}pt)</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-slate-600 text-xs">Ospite</Label>
            <Select value={autoAway} onValueChange={setAutoAway}>
              <SelectTrigger className="bg-slate-50 border-slate-200 mt-1" data-testid="auto-away"><SelectValue placeholder="Squadra" /></SelectTrigger>
              <SelectContent>
                {teams.map((t) => <SelectItem key={t.user_id} value={t.user_id}>{t.team_name} · {t.girone} ({t.points.toFixed(1)}pt)</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-end">
            <Button onClick={createAuto} disabled={busy} className="btn-brand rounded-full w-full" data-testid="auto-create">
              {busy ? "Calcolo..." : "Genera mercato"}
            </Button>
          </div>
        </div>
      </div>

      {/* Manual market */}
      <div className="card-surface rounded-2xl p-5 space-y-3">
        <div className="flex items-center gap-2">
          <Dice5 className="w-5 h-5" style={{ color: "var(--scommetti)" }} />
          <div className="display font-bold text-lg">Crea mercato manuale</div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="md:col-span-2">
            <Label className="text-slate-600 text-xs">Titolo</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="es. Miglior fantallenatore giornata 3"
                   className="bg-slate-50 border-slate-200 mt-1" data-testid="market-title" />
          </div>
          <div>
            <Label className="text-slate-600 text-xs">Giornata</Label>
            <Input type="number" min={1} value={matchday} onChange={(e) => setMatchday(e.target.value)}
                   className="bg-slate-50 border-slate-200 mt-1" data-testid="market-matchday" />
          </div>
        </div>
        <div className="space-y-2">
          {options.map((o, i) => (
            <div key={i} className="flex items-center gap-2">
              <Input value={o.label} onChange={(e) => setOptions((arr) => arr.map((x, idx) => idx === i ? { ...x, label: e.target.value } : x))}
                     placeholder={`Opzione ${i + 1}`} className="bg-slate-50 border-slate-200 flex-1" data-testid={`opt-label-${i}`} />
              <Input type="number" step="0.05" min={1.01} value={o.odds}
                     onChange={(e) => setOptions((arr) => arr.map((x, idx) => idx === i ? { ...x, odds: e.target.value } : x))}
                     className="bg-slate-50 border-slate-200 w-24" data-testid={`opt-odds-${i}`} />
              {options.length > 2 && (
                <button onClick={() => removeOption(i)} className="p-2 text-slate-400 hover:text-slate-900"><X className="w-4 h-4" /></button>
              )}
            </div>
          ))}
        </div>
        <div className="flex gap-2 justify-between">
          <Button variant="outline" onClick={addOption} className="border-slate-300 rounded-full">
            <PlusCircle className="w-4 h-4 mr-2" /> Aggiungi opzione
          </Button>
          <Button onClick={create} disabled={busy} className="btn-brand rounded-full" data-testid="market-create">
            {busy ? "Creazione..." : "Crea mercato"}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {markets.map((m) => (
          <div key={m.id} className="card-surface rounded-2xl p-4">
            <div className="flex items-start justify-between gap-2">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 flex items-center gap-2">
                  G{m.matchday} · {m.status}
                  {m.auto && <span className="px-1.5 py-0.5 rounded-full text-[9px]" style={{ background: "var(--brand)22", color: "var(--brand)" }}>AUTO</span>}
                </div>
                <div className="display font-bold">{m.title}</div>
              </div>
              {m.status === "open" && (
                <Button size="sm" variant="outline" onClick={() => closeMarket(m.id)} className="border-slate-300 rounded-full text-xs">Chiudi</Button>
              )}
            </div>
            <div className="mt-3 space-y-2">
              {m.options.map((o) => (
                <div key={o.id} className="flex items-center justify-between border border-slate-200 rounded-lg px-3 py-2">
                  <span className="text-sm">{o.label} <span className="text-slate-400 text-xs">x{o.odds}</span></span>
                  {m.status !== "settled" ? (
                    <Button size="sm" variant="outline" onClick={() => settleMarket(m, o.id)}
                            className="border-slate-300 rounded-full text-xs" data-testid={`settle-${m.id}-${o.id}`}>
                      Vincente
                    </Button>
                  ) : m.winning_option_id === o.id ? (
                    <Badge className="bg-[var(--brand)] text-white">Vinta</Badge>
                  ) : null}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}


/* ============ ASTA TAB — upload rose per girone (4 Excel) ============ */
function AstaTab() {
  const [status, setStatus] = useState({ A: {}, B: {}, C: {}, D: {} });
  const [busy, setBusy] = useState(null); // girone code while uploading
  const [wiping, setWiping] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/admin/asta/status"); setStatus(data); } catch {}
  };
  useEffect(() => { load(); }, []);

  const uploadOne = async (g, file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".xlsx")) {
      toast.error("Serve un file .xlsx");
      return;
    }
    setBusy(g);
    const fd = new FormData();
    fd.append("girone", g);
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/asta/upload", fd);
      toast.success(`Girone ${g}: ${data.teams_count} squadre caricate`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload fallito");
    } finally {
      setBusy(null);
    }
  };

  const wipeMock = async () => {
    if (!window.confirm("Cancello davvero tutte le squadre finte (demo)? L'admin non verrà toccato.")) return;
    setWiping(true);
    try {
      const { data } = await api.post("/admin/asta/wipe-mock");
      toast.success(`Rimosse ${data.removed_users} squadre demo`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    } finally {
      setWiping(false);
    }
  };

  const totalTeams = GIRONI.reduce((sum, g) => sum + (status[g]?.teams_count || 0), 0);
  const totalPlayers = GIRONI.reduce((sum, g) => sum + (status[g]?.players_total || 0), 0);

  return (
    <div className="space-y-6" data-testid="admin-asta-panel">
      <div className="card-surface rounded-2xl p-5 md:p-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-xs uppercase tracking-widest text-slate-500">Rose ufficiali di stagione</div>
            <h2 className="display font-black text-2xl md:text-3xl mt-1">Upload Aste per Girone</h2>
            <p className="text-slate-600 text-sm md:text-base mt-2 max-w-2xl">
              Carica i 4 file Excel dell'asta (uno per girone). Ogni file contiene 8 squadre × 29 giocatori (4P/9D/9C/7A) con relativo costo.
              I giocatori più pagati verranno calcolati come media dei costi su tutte le occorrenze.
            </p>
          </div>
          <div className="flex gap-2">
            <div className="card-surface rounded-xl px-4 py-2 text-center">
              <div className="text-[10px] uppercase tracking-widest text-slate-500">Squadre</div>
              <div className="display font-black text-2xl" data-testid="asta-total-teams">{totalTeams}<span className="text-slate-400 text-sm">/32</span></div>
            </div>
            <div className="card-surface rounded-xl px-4 py-2 text-center">
              <div className="text-[10px] uppercase tracking-widest text-slate-500">Giocatori</div>
              <div className="display font-black text-2xl" data-testid="asta-total-players">{totalPlayers}</div>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {GIRONI.map((g) => {
          const s = status[g] || {};
          const done = (s.teams_count || 0) > 0;
          return (
            <div key={g} className={`card-surface rounded-2xl p-5 ${done ? "border-[var(--brand)]/40" : ""}`} data-testid={`asta-card-${g}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <GironeBadge girone={g} size={40} />
                  <div>
                    <div className="display font-bold text-lg">Girone {g}</div>
                    <div className="text-xs text-slate-500">{s.teams_count || 0}/8 squadre · {s.players_total || 0} giocatori</div>
                  </div>
                </div>
                {done && <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200"><CheckCircle2 className="w-3.5 h-3.5 mr-1" />Caricato</Badge>}
              </div>

              {s.team_names?.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {s.team_names.map((tn) => (
                    <span key={tn} className="text-[11px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 border border-slate-200">{tn}</span>
                  ))}
                </div>
              )}

              <label className="mt-4 flex items-center gap-2 cursor-pointer" htmlFor={`asta-file-${g}`}>
                <input
                  id={`asta-file-${g}`}
                  type="file"
                  accept=".xlsx"
                  className="hidden"
                  onChange={(e) => uploadOne(g, e.target.files?.[0])}
                  data-testid={`asta-file-${g}`}
                />
                <Button
                  type="button"
                  onClick={() => document.getElementById(`asta-file-${g}`)?.click()}
                  className="btn-brand rounded-full h-10 px-5"
                  disabled={busy === g}
                  data-testid={`asta-upload-${g}`}
                >
                  <Upload className="w-4 h-4 mr-2" />
                  {busy === g ? "Caricamento…" : done ? `Sostituisci Excel Girone ${g}` : `Carica Excel Girone ${g}`}
                </Button>
                <span className="text-xs text-slate-400">.xlsx</span>
              </label>
            </div>
          );
        })}
      </div>

      <div className="card-surface rounded-2xl p-5 border-orange-200 bg-orange-50/40">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="display font-bold text-base">Pulizia squadre demo</div>
            <p className="text-slate-600 text-sm mt-1 max-w-xl">
              Rimuove tutti gli utenti/squadre finti creati con il seeder. Utile prima di rendere pubbliche le squadre reali dell'asta. L'admin resta intatto.
            </p>
          </div>
          <Button
            onClick={wipeMock}
            disabled={wiping}
            variant="outline"
            className="border-orange-300 text-orange-700 hover:bg-orange-100 rounded-full h-10"
            data-testid="asta-wipe-mock"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            {wiping ? "Rimozione…" : "Cancella squadre demo"}
          </Button>
        </div>
      </div>
    </div>
  );
}


/* ============ COPPA MAXI TAB — sorteggio + risultati ============ */
function CoppaMaxiTab() {
  const [rounds, setRounds] = useState([]);
  const [standings, setStandings] = useState([]);
  const [busy, setBusy] = useState(false);
  const [activeRound, setActiveRound] = useState("1");
  const [config, setConfig] = useState({ schedule: [], thresholds: [] });
  const [scheduleInput, setScheduleInput] = useState({});
  const [thresholdsInput, setThresholdsInput] = useState("");
  const [midThresholdsInput, setMidThresholdsInput] = useState("");
  const [useCupMidfield, setUseCupMidfield] = useState(true);

  const load = async () => {
    try {
      const [m, s, c] = await Promise.all([
        api.get("/public/coppa/maxi/matches"),
        api.get("/public/coppa/maxi/standings"),
        api.get("/public/coppa/maxi/config"),
      ]);
      setRounds(m.data || []);
      setStandings(s.data || []);
      setConfig(c.data || { schedule: [], thresholds: [] });
      const schedMap = {};
      (c.data?.schedule || []).forEach((it) => { schedMap[it.round] = it.campionato_matchday; });
      setScheduleInput(schedMap);
      setThresholdsInput((c.data?.thresholds || []).join(", "));
      setMidThresholdsInput((c.data?.midfield_thresholds || []).map((p) => `${p[0]}:${p[1]}`).join(", "));
      setUseCupMidfield(c.data?.use_cup_midfield !== false);
      if (m.data?.[0]) setActiveRound(String(m.data[0].round));
    } catch {}
  };
  useEffect(() => { load(); }, []);

  const saveConfig = async () => {
    const schedule = Object.entries(scheduleInput)
      .filter(([, v]) => v && parseInt(v, 10) > 0)
      .map(([r, v]) => ({ round: parseInt(r, 10), campionato_matchday: parseInt(v, 10) }));
    const thresholds = (thresholdsInput || "")
      .split(",").map((s) => parseInt(s.trim(), 10)).filter((n) => n > 0);
    const midfield_thresholds = (midThresholdsInput || "")
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean)
      .map((s) => {
        const [d, b] = s.split(":").map((x) => parseFloat(x));
        return [d, b];
      })
      .filter((p) => !isNaN(p[0]) && !isNaN(p[1]));
    try {
      await api.put("/admin/coppa/maxi/config", {
        schedule, thresholds, midfield_thresholds, use_cup_midfield: useCupMidfield,
      });
      toast.success("Configurazione salvata");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore salvataggio");
    }
  };

  const computeRound = async (r) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/coppa/maxi/compute-round/${r}`);
      toast.success(`G${r} calcolata: ${data.updated} partite (MD Campionato ${data.campionato_matchday})`);
      if (data.missing?.length) toast.warning(`${data.missing.length} match senza punteggi`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore calcolo");
    } finally { setBusy(false); }
  };
  const computeAll = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/coppa/maxi/compute-all");
      const ok = (data.results || []).filter((r) => !r.error).length;
      toast.success(`Calcolate ${ok} giornate Coppa`);
      await load();
    } catch (e) {
      toast.error("Errore compute-all");
    } finally { setBusy(false); }
  };

  const sorteggia = async () => {
    if (!window.confirm("Rigenero il maxi girone? Tutte le partite attuali saranno sostituite.")) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/coppa/maxi/sorteggio");
      toast.success(`Sorteggio completato: ${data.matches} partite in ${data.rounds} giornate`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sorteggio fallito");
    } finally { setBusy(false); }
  };
  const reset = async () => {
    if (!window.confirm("Cancello tutte le partite del maxi girone?")) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/coppa/maxi/reset");
      toast.success(`Rimosse ${data.removed} partite`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore reset");
    } finally { setBusy(false); }
  };

  const totalMatches = rounds.reduce((n, r) => n + (r.matches?.length || 0), 0);

  return (
    <div className="space-y-6" data-testid="admin-coppa-maxi-panel">
      <div className="card-surface rounded-2xl p-5 flex flex-wrap gap-3 items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500">Coppa Maxi · Champions FIFE</div>
          <div className="display font-black text-xl mt-1">{totalMatches} partite · {rounds.length} giornate</div>
        </div>
        <div className="flex gap-2">
          <Button onClick={sorteggia} disabled={busy} className="btn-brand rounded-full" data-testid="coppa-maxi-sorteggio">
            <Shuffle className="w-4 h-4 mr-2" /> {totalMatches > 0 ? "Rifai sorteggio" : "Lancia sorteggio"}
          </Button>
          {totalMatches > 0 && (
            <Button onClick={reset} disabled={busy} variant="outline" className="rounded-full border-slate-300" data-testid="coppa-maxi-reset">
              <X className="w-4 h-4 mr-2" /> Reset
            </Button>
          )}
        </div>
      </div>

      {rounds.length > 0 && (
        <div className="rounded-2xl border-2 border-blue-300 bg-blue-50 p-5" data-testid="coppa-maxi-schedule-editor">
          <div className="flex items-start gap-3 flex-wrap">
            <Trophy className="w-6 h-6 text-blue-700 shrink-0 mt-1" />
            <div className="flex-1 min-w-0">
              <div className="display font-black text-lg text-blue-900">Schedule Coppa ↔ Campionato + Soglie gol</div>
              <p className="text-blue-900/80 text-sm mt-1">
                Per ogni giornata di Coppa specifica la giornata di Campionato FIFE dalla quale prendere i punteggi.
                I punti vengono convertiti in gol usando le soglie qui sotto (default: 66, 72, 77, poi +4).
              </p>
            </div>
          </div>
          <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2">
            {rounds.map((r) => (
              <div key={r.round} className="bg-white rounded-lg p-2 border border-blue-200 min-w-0">
                <div className="text-[10px] uppercase text-blue-700 font-bold truncate">G{r.round} Coppa</div>
                <Input type="number" inputMode="numeric" min={1} value={scheduleInput[r.round] || ""}
                       onChange={(e) => setScheduleInput({ ...scheduleInput, [r.round]: e.target.value })}
                       placeholder="MD FIFE" className="mt-1 text-sm h-9"
                       data-testid={`schedule-round-${r.round}`} />
              </div>
            ))}
          </div>
          <div className="mt-4">
            <div className="text-xs uppercase tracking-widest text-blue-900 font-bold mb-1">Soglie gol (punti separati da virgola)</div>
            <Input value={thresholdsInput} onChange={(e) => setThresholdsInput(e.target.value)}
                   placeholder="66, 72, 77, 81, 85, 89, 93, 97, 101, 105, 109, 113, 117"
                   className="bg-white" data-testid="schedule-thresholds" />
          </div>
          <div className="mt-4">
            <div className="flex items-center gap-3 flex-wrap">
              <label className="flex items-center gap-2 text-sm text-blue-900 cursor-pointer" data-testid="use-cup-midfield-toggle">
                <input type="checkbox" checked={useCupMidfield}
                       onChange={(e) => setUseCupMidfield(e.target.checked)}
                       className="w-4 h-4 accent-blue-600" />
                <b>Applica modificatore centrocampo Coppa</b> (ricalcolato in base al match Coppa)
              </label>
            </div>
            {useCupMidfield && (
              <div className="mt-2">
                <div className="text-xs uppercase tracking-widest text-blue-900 font-bold mb-1">
                  Soglie modificatore centrocampo (diff_voti:bonus separate da virgola)
                </div>
                <Input value={midThresholdsInput} onChange={(e) => setMidThresholdsInput(e.target.value)}
                       placeholder="2:1, 3:2, 4:2, 5:3, 6:3.5, 8:4"
                       className="bg-white text-xs" data-testid="schedule-mid-thresholds" />
                <div className="text-[10px] text-blue-800 mt-1">
                  Formato: <code>diffAvg:bonus</code>. Default identico al campionato.
                </div>
              </div>
            )}
          </div>
          <div className="mt-4 flex gap-2 flex-wrap">
            <Button onClick={saveConfig} className="bg-blue-600 hover:bg-blue-700 text-white rounded-full" data-testid="save-schedule-btn">
              <Save className="w-4 h-4 mr-1" /> Salva configurazione
            </Button>
            <Button onClick={computeAll} disabled={busy || (config.schedule || []).length === 0}
                    variant="outline" className="rounded-full border-blue-300 text-blue-700" data-testid="compute-all-btn">
              <Dice5 className="w-4 h-4 mr-1" /> Calcola tutte le giornate configurate
            </Button>
          </div>
        </div>
      )}

      {rounds.length > 0 && (
        <div className="card-surface rounded-2xl p-5">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-3">Inserimento risultati</div>
          <Tabs value={activeRound} onValueChange={setActiveRound}>
            <TabsList className="bg-slate-50 border border-slate-200 flex overflow-x-auto whitespace-nowrap h-auto p-1 gap-1 max-w-full [&>button]:shrink-0 [&::-webkit-scrollbar]:h-1">
              {rounds.map((r) => (
                <TabsTrigger key={r.round} value={String(r.round)} className="data-[state=active]:bg-[var(--brand)] data-[state=active]:text-white" data-testid={`admin-maxi-round-${r.round}`}>
                  G{r.round}
                </TabsTrigger>
              ))}
            </TabsList>
            {rounds.map((r) => (
              <TabsContent key={r.round} value={String(r.round)} className="mt-4">
                <div className="mb-3 flex items-center justify-between flex-wrap gap-2">
                  <div className="text-sm text-slate-600">
                    {r.campionato_matchday
                      ? <>Punteggi da <b>MD Campionato {r.campionato_matchday}</b></>
                      : <span className="text-amber-600">⚠ Nessuna MD associata</span>}
                  </div>
                  {r.campionato_matchday && (
                    <Button size="sm" onClick={() => computeRound(r.round)} disabled={busy}
                            className="btn-brand rounded-full" data-testid={`compute-round-${r.round}`}>
                      <Dice5 className="w-3.5 h-3.5 mr-1" /> Calcola G{r.round}
                    </Button>
                  )}
                </div>
                <div className="grid grid-cols-1 gap-2">
                  {r.matches.map((m) => <AdminMaxiMatchRow key={m.id} m={m} onSaved={load} />)}
                </div>
              </TabsContent>
            ))}
          </Tabs>
        </div>
      )}

      {standings.length > 0 && (
        <div className="card-surface rounded-2xl p-5">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-3">Classifica live (3/1/0)</div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-slate-500 uppercase text-[10px] tracking-widest">
                <tr>
                  <th className="text-left px-2 py-1 w-10">#</th>
                  <th className="text-left px-2 py-1">Squadra</th>
                  <th className="text-center px-2 py-1">PG</th>
                  <th className="text-center px-2 py-1">V</th>
                  <th className="text-center px-2 py-1">N</th>
                  <th className="text-center px-2 py-1">P</th>
                  <th className="text-right px-2 py-1">PT</th>
                </tr>
              </thead>
              <tbody>
                {standings.slice(0, 12).map((t) => (
                  <tr key={t.team_id} className="border-t border-slate-100">
                    <td className="px-2 py-1 text-slate-500 font-bold">{t.rank}</td>
                    <td className="px-2 py-1 flex items-center gap-2">
                      <GironeBadge girone={t.girone} size={16} />
                      <span className="truncate">{t.team_name}</span>
                    </td>
                    <td className="text-center px-2 py-1 tabular-nums">{t.played}</td>
                    <td className="text-center px-2 py-1 text-emerald-600 font-bold tabular-nums">{t.wins}</td>
                    <td className="text-center px-2 py-1 tabular-nums">{t.draws}</td>
                    <td className="text-center px-2 py-1 text-rose-500 font-bold tabular-nums">{t.losses}</td>
                    <td className="text-right px-2 py-1 font-black tabular-nums" style={{ color: "var(--brand)" }}>{t.points}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <PlayoffAdminSection />
      <OttaviAdminSection />
      <EuropaOttaviAdminSection />
    </div>
  );
}

function EuropaOttaviAdminSection() {
  const [matches, setMatches] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = async () => {
    try { const { data } = await api.get("/public/coppa/europa-ottavi/matches"); setMatches(data || []); } catch {}
  };
  useEffect(() => { load(); }, []);
  const sorteggia = async () => {
    if (matches.length && !window.confirm("Rifare il sorteggio ottavi EL? Perdi tutti i risultati attuali.")) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/coppa/europa-ottavi/sorteggio");
      toast.success(`Sorteggio ottavi EL: ${data.matches} partite`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sorteggio ottavi EL fallito");
    } finally { setBusy(false); }
  };
  const reset = async () => {
    if (!window.confirm("Cancello ottavi EL?")) return;
    setBusy(true);
    try { await api.post("/admin/coppa/europa-ottavi/reset"); toast.success("Ottavi EL resettati"); await load(); }
    catch { toast.error("Errore reset"); }
    finally { setBusy(false); }
  };
  const saveResult = async (id, hs, as_) => {
    try {
      await api.post(`/admin/coppa/europa-ottavi/result/${id}`, { home_score: Number(hs), away_score: Number(as_) });
      toast.success("Ottavo EL salvato");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    }
  };
  return (
    <div className="card-surface rounded-2xl p-5" data-testid="admin-europa-ottavi-section">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500">Ottavi Europa League (perdenti Playoff + 25ª-32ª)</div>
          <div className="display font-black text-lg">{matches.length}/8 sfide</div>
        </div>
        <div className="flex gap-2">
          <Button onClick={sorteggia} disabled={busy} className="rounded-full text-white" style={{ background: "#f97316" }} data-testid="europa-ottavi-sorteggio">
            <Rocket className="w-4 h-4 mr-2" /> {matches.length > 0 ? "Rifai sorteggio" : "Sorteggia Ottavi EL"}
          </Button>
          {matches.length > 0 && (
            <Button onClick={reset} disabled={busy} variant="outline" className="rounded-full border-slate-300" data-testid="europa-ottavi-reset">
              <X className="w-4 h-4 mr-2" /> Reset
            </Button>
          )}
        </div>
      </div>
      {matches.length > 0 && (
        <div className="mt-4 grid grid-cols-1 gap-2">
          {matches.map((m) => <AdminTwoLegRow key={m.id} m={m} endpoint="/admin/coppa/europa-ottavi/result" onSave={load} label="europa-ottavi" />)}
        </div>
      )}
    </div>
  );
}

function OttaviAdminSection() {
  const [matches, setMatches] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = async () => {
    try { const { data } = await api.get("/public/coppa/ottavi/matches"); setMatches(data || []); } catch {}
  };
  useEffect(() => { load(); }, []);
  const sorteggia = async () => {
    if (matches.length && !window.confirm("Rifare il sorteggio ottavi? Perdi tutti i risultati attuali.")) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/coppa/ottavi/sorteggio");
      toast.success(`Sorteggio ottavi: ${data.matches} partite`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sorteggio ottavi fallito");
    } finally { setBusy(false); }
  };
  const reset = async () => {
    if (!window.confirm("Cancello ottavi?")) return;
    setBusy(true);
    try { await api.post("/admin/coppa/ottavi/reset"); toast.success("Ottavi resettati"); await load(); }
    catch { toast.error("Errore reset"); }
    finally { setBusy(false); }
  };
  const saveResult = async (id, hs, as_) => {
    try {
      await api.post(`/admin/coppa/ottavi/result/${id}`, { home_score: Number(hs), away_score: Number(as_) });
      toast.success("Ottavo salvato");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    }
  };
  return (
    <div className="card-surface rounded-2xl p-5" data-testid="admin-ottavi-section">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500">Ottavi Champions (top-8 + vincenti Playoff)</div>
          <div className="display font-black text-lg">{matches.length}/8 sfide</div>
        </div>
        <div className="flex gap-2">
          <Button onClick={sorteggia} disabled={busy} className="btn-brand rounded-full" data-testid="ottavi-sorteggio">
            <Trophy className="w-4 h-4 mr-2" /> {matches.length > 0 ? "Rifai sorteggio" : "Sorteggia Ottavi"}
          </Button>
          {matches.length > 0 && (
            <Button onClick={reset} disabled={busy} variant="outline" className="rounded-full border-slate-300" data-testid="ottavi-reset">
              <X className="w-4 h-4 mr-2" /> Reset
            </Button>
          )}
        </div>
      </div>
      {matches.length > 0 && (
        <div className="mt-4 grid grid-cols-1 gap-2">
          {matches.map((m) => <AdminBracketRow key={m.id} m={m} onSave={saveResult} label="ottavi" />)}
        </div>
      )}
    </div>
  );
}

function AdminTwoLegRow({ m, endpoint, onSave, label, accent }) {
  const [l1h, setL1h] = useState(m.leg1_home ?? "");
  const [l1a, setL1a] = useState(m.leg1_away ?? "");
  const [l2h, setL2h] = useState(m.leg2_home ?? "");
  const [l2a, setL2a] = useState(m.leg2_away ?? "");
  const [d1, setD1] = useState(m.date_leg1 ?? "");
  const [d2, setD2] = useState(m.date_leg2 ?? "");
  const [mdA, setMdA] = useState(m.matchday_leg1 ?? "");
  const [mdR, setMdR] = useState(m.matchday_leg2 ?? "");
  const [computing, setComputing] = useState(false);
  // label è "playoff" | "ottavi" | "europa-ottavi" → coincide con il segmento
  // {competition} dell'endpoint compute /admin/coppa/two-leg/{competition}/compute/{id}
  const competition = label;
  const submit = async () => {
    if ([l1h, l1a, l2h, l2a].some((v) => v === "")) return toast.error("Compila entrambi i punteggi di andata e ritorno");
    try {
      await api.post(`${endpoint}/${m.id}`, {
        leg1_home: Number(l1h), leg1_away: Number(l1a),
        leg2_home: Number(l2h), leg2_away: Number(l2a),
        date_leg1: d1 || null, date_leg2: d2 || null,
      });
      toast.success("Doppia sfida salvata");
      onSave && onSave();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore salvataggio");
    }
  };
  const autoCompute = async () => {
    const nA = Number(mdA), nR = Number(mdR);
    if (!nA || !nR || nA < 1 || nR < 1 || nA > 38 || nR > 38) {
      return toast.error("Indica due giornate Campionato valide (1-38) per andata e ritorno");
    }
    if (nA === nR) {
      return toast.error("Andata e ritorno devono essere su due giornate diverse");
    }
    setComputing(true);
    try {
      const { data } = await api.post(
        `/admin/coppa/two-leg/${competition}/compute/${m.id}`,
        { matchday_leg1: nA, matchday_leg2: nR },
      );
      // Ricarica la lista dal server (il doc è già stato aggiornato lato backend)
      toast.success(
        `Doppia sfida calcolata · Aggregate ${data.aggregate} · G${nA}↔G${nR}`,
        { description: `Vincitore: ${data.winner_id === m.home_id ? m.home_name : m.away_name}` },
      );
      onSave && onSave();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Compute automatico fallito");
    } finally {
      setComputing(false);
    }
  };
  return (
    <div className="border border-slate-200 rounded-xl p-3" data-testid={`admin-twoleg-${label}-${m.id}`}>
      <div className="flex items-center gap-2 mb-3 text-xs text-slate-700 flex-wrap">
        <span className="text-[10px] font-black px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">#{m.seed_home}</span>
        <GironeBadge girone={m.home_girone} size={16} />
        <span className="truncate max-w-[180px] font-semibold">{m.home_name}</span>
        <span className="text-slate-400">vs</span>
        <GironeBadge girone={m.away_girone} size={16} />
        <span className="truncate max-w-[180px] font-semibold">{m.away_name}</span>
        <span className="text-[10px] font-black px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">#{m.seed_away}</span>
        {m.status === "played" && m.cup_midfield_applied && (
          <span className="ml-auto text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200"
                title="Il modificatore centrocampo è stato ricalcolato in base all'abbinamento Coppa">
            MOD. CENTROCAMPO COPPA ✓
          </span>
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        <div className="flex items-center gap-2">
          <span className="text-[10px] uppercase tracking-widest text-slate-500 w-16 shrink-0">Andata</span>
          <Input type="date" value={d1} onChange={(e) => setD1(e.target.value)} className="h-9 flex-1 bg-slate-50" data-testid={`${label}-d1-${m.id}`} />
          <Input type="number" min="0" value={l1h} onChange={(e) => setL1h(e.target.value)} className="h-9 w-12 text-center bg-slate-50" placeholder="H" data-testid={`${label}-l1h-${m.id}`} />
          <span className="text-slate-300">–</span>
          <Input type="number" min="0" value={l1a} onChange={(e) => setL1a(e.target.value)} className="h-9 w-12 text-center bg-slate-50" placeholder="A" data-testid={`${label}-l1a-${m.id}`} />
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[10px] uppercase tracking-widest text-slate-500 w-16 shrink-0">Ritorno</span>
          <Input type="date" value={d2} onChange={(e) => setD2(e.target.value)} className="h-9 flex-1 bg-slate-50" data-testid={`${label}-d2-${m.id}`} />
          <Input type="number" min="0" value={l2h} onChange={(e) => setL2h(e.target.value)} className="h-9 w-12 text-center bg-slate-50" placeholder="H" data-testid={`${label}-l2h-${m.id}`} />
          <span className="text-slate-300">–</span>
          <Input type="number" min="0" value={l2a} onChange={(e) => setL2a(e.target.value)} className="h-9 w-12 text-center bg-slate-50" placeholder="A" data-testid={`${label}-l2a-${m.id}`} />
        </div>
      </div>
      <div className="flex justify-end mt-2">
        <Button size="sm" onClick={submit} className="btn-brand rounded-full h-8" data-testid={`${label}-save-${m.id}`}>
          <Save className="w-4 h-4 mr-1" /> Salva doppia sfida
        </Button>
      </div>

      {/* --- Auto-compute two-leg da Campionato con ricalcolo modificatore centrocampo Coppa --- */}
      <div className="mt-3 pt-3 border-t border-dashed border-blue-200 bg-blue-50/40 -mx-3 -mb-3 px-3 pb-3 rounded-b-xl">
        <div className="flex items-center gap-2 mb-2">
          <Sparkles className="w-3.5 h-3.5 text-blue-700 shrink-0" />
          <div className="text-[10px] uppercase tracking-widest text-blue-800 font-bold">
            Compute automatico da Campionato
          </div>
          <span className="text-[10px] text-blue-600 hidden sm:inline">
            · rimuove il mod. centrocampo di campionato e ricalcola quello di Coppa sull'abbinamento
          </span>
        </div>
        <div className="flex items-end gap-2 flex-wrap">
          <div className="flex flex-col">
            <label className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">MD Andata</label>
            <Input
              type="number" min="1" max="38"
              value={mdA} onChange={(e) => setMdA(e.target.value)}
              className="h-9 w-20 text-center bg-white"
              placeholder="es. 1"
              data-testid={`${label}-md1-${m.id}`}
            />
          </div>
          <div className="flex flex-col">
            <label className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">MD Ritorno</label>
            <Input
              type="number" min="1" max="38"
              value={mdR} onChange={(e) => setMdR(e.target.value)}
              className="h-9 w-20 text-center bg-white"
              placeholder="es. 2"
              data-testid={`${label}-md2-${m.id}`}
            />
          </div>
          <Button
            size="sm" onClick={autoCompute} disabled={computing}
            className="rounded-full h-9 text-white"
            style={{ background: "#1d4ed8" }}
            data-testid={`${label}-compute-${m.id}`}
          >
            <Sparkles className="w-4 h-4 mr-1" />
            {computing ? "Calcolo…" : "Calcola da Campionato"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function AdminBracketRow({ m, onSave, label }) {
  const [hs, setHs] = useState(m.home_score ?? "");
  const [as_, setAs] = useState(m.away_score ?? "");
  const submit = () => {
    if (hs === "" || as_ === "") return toast.error("Compila entrambi i punteggi");
    if (Number(hs) === Number(as_)) return toast.error("Non ammesso il pareggio");
    onSave(m.id, hs, as_);
  };
  return (
    <div className="grid grid-cols-[auto_1fr_auto_auto_auto_1fr_auto_auto] items-center gap-2 border border-slate-200 rounded-xl px-3 py-2" data-testid={`admin-${label}-${m.id}`}>
      <span className="text-[10px] font-black px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">#{m.seed_home}</span>
      <div className="flex items-center gap-2 min-w-0">
        <GironeBadge girone={m.home_girone} size={16} />
        <span className="text-sm truncate">{m.home_name}</span>
      </div>
      <Input type="number" min="0" value={hs} onChange={(e) => setHs(e.target.value)} className="h-9 w-16 text-center bg-slate-50" />
      <span className="text-slate-300">–</span>
      <Input type="number" min="0" value={as_} onChange={(e) => setAs(e.target.value)} className="h-9 w-16 text-center bg-slate-50" />
      <div className="flex items-center gap-2 justify-end min-w-0">
        <span className="text-sm truncate text-right">{m.away_name}</span>
        <GironeBadge girone={m.away_girone} size={16} />
      </div>
      <span className="text-[10px] font-black px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">#{m.seed_away}</span>
      <Button size="sm" onClick={submit} className="btn-brand rounded-full h-9" data-testid={`${label}-save-${m.id}`}>
        <Save className="w-4 h-4" />
      </Button>
    </div>
  );
}

function PlayoffAdminSection() {
  const [matches, setMatches] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = async () => {
    try { const { data } = await api.get("/public/coppa/playoff/matches"); setMatches(data || []); } catch {}
  };
  useEffect(() => { load(); }, []);

  const sorteggia = async () => {
    if (matches.length && !window.confirm("Rifare il sorteggio playoff? Perdi tutti i risultati attuali.")) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/coppa/playoff/sorteggio");
      toast.success(`Sorteggio playoff completato: ${data.matches} partite`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sorteggio playoff fallito");
    } finally { setBusy(false); }
  };
  const reset = async () => {
    if (!window.confirm("Cancello tutte le partite playoff?")) return;
    setBusy(true);
    try {
      await api.post("/admin/coppa/playoff/reset");
      toast.success("Playoff resettati");
      await load();
    } catch (e) {
      toast.error("Errore reset");
    } finally { setBusy(false); }
  };

  return (
    <div className="card-surface rounded-2xl p-5" data-testid="admin-playoff-section">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500">Playoff Champions (9ª-24ª)</div>
          <div className="display font-black text-lg">{matches.length}/8 sfide</div>
        </div>
        <div className="flex gap-2">
          <Button onClick={sorteggia} disabled={busy} className="btn-brand rounded-full" data-testid="playoff-sorteggio">
            <Shuffle className="w-4 h-4 mr-2" /> {matches.length > 0 ? "Rifai sorteggio" : "Lancia sorteggio"}
          </Button>
          {matches.length > 0 && (
            <Button onClick={reset} disabled={busy} variant="outline" className="rounded-full border-slate-300" data-testid="playoff-reset">
              <X className="w-4 h-4 mr-2" /> Reset
            </Button>
          )}
        </div>
      </div>
      {matches.length > 0 && (
        <div className="mt-4 grid grid-cols-1 gap-2">
          {matches.map((m) => <AdminTwoLegRow key={m.id} m={m} endpoint="/admin/coppa/playoff/result" onSave={load} label="playoff" />)}
        </div>
      )}
    </div>
  );
}

function AdminPlayoffRow_UNUSED() { return null; }

function AdminMaxiMatchRow({ m, onSaved }) {
  const [hs, setHs] = useState(m.home_score ?? "");
  const [as_, setAs] = useState(m.away_score ?? "");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (hs === "" || as_ === "") return toast.error("Compila entrambi i punteggi");
    setBusy(true);
    try {
      await api.post(`/admin/coppa/maxi/result/${m.id}`, { home_score: Number(hs), away_score: Number(as_) });
      toast.success("Risultato salvato");
      onSaved && onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore salvataggio");
    } finally { setBusy(false); }
  };
  return (
    <div className="grid grid-cols-[1fr_auto_auto_auto_1fr_auto] items-center gap-2 border border-slate-200 rounded-xl px-3 py-2" data-testid={`admin-maxi-match-${m.id}`}>
      <div className="flex items-center gap-2 min-w-0">
        <GironeBadge girone={m.home_girone} size={18} />
        <span className="text-sm truncate">{m.home_name}</span>
      </div>
      <Input type="number" min="0" value={hs} onChange={(e) => setHs(e.target.value)} className="h-9 w-16 text-center bg-slate-50" data-testid={`maxi-hs-${m.id}`} />
      <span className="text-slate-300">–</span>
      <Input type="number" min="0" value={as_} onChange={(e) => setAs(e.target.value)} className="h-9 w-16 text-center bg-slate-50" data-testid={`maxi-as-${m.id}`} />
      <div className="flex items-center gap-2 justify-end min-w-0">
        <span className="text-sm truncate text-right">{m.away_name}</span>
        <GironeBadge girone={m.away_girone} size={18} />
      </div>
      <Button size="sm" onClick={submit} disabled={busy} className="btn-brand rounded-full h-9" data-testid={`maxi-save-${m.id}`}>
        <Save className="w-4 h-4" />
      </Button>
    </div>
  );
}

// ============ PUSH TAB ============
function PushTab() {
  const [subs, setSubs] = useState({ total: 0, items: [] });
  const [scope, setScope] = useState("broadcast"); // "broadcast" | "user"
  const [userId, setUserId] = useState("");
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [url, setUrl] = useState("/dashboard");
  const [busy, setBusy] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const loadSubs = async () => {
    try {
      const { data } = await api.get("/admin/push/subscribers");
      setSubs(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Impossibile caricare gli iscritti push");
    }
  };
  useEffect(() => { loadSubs(); }, []);

  const send = async () => {
    if (!title.trim() || !body.trim()) {
      return toast.error("Titolo e testo sono obbligatori");
    }
    if (scope === "user" && !userId) {
      return toast.error("Scegli un fantallenatore destinatario");
    }
    setBusy(true); setLastResult(null);
    try {
      const payload = {
        title: title.trim(),
        body: body.trim(),
        url: (url || "/").trim() || "/",
      };
      if (scope === "broadcast") payload.broadcast = true;
      if (scope === "user") payload.user_id = userId;
      const { data } = await api.post("/admin/push/test", payload);
      setLastResult(data);
      if (data?.ok) {
        toast.success(
          scope === "broadcast"
            ? `Broadcast inviato · ${data.sent} push a ${data.users} fantallenatori`
            : `Push inviata · ${data.sent} device raggiunti`
        );
      } else {
        toast.error(data?.detail || "Invio fallito");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore invio push");
    } finally {
      setBusy(false);
    }
  };

  const totalDevices = subs.items.reduce((acc, s) => acc + (s.subs_count || 0), 0);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-6" data-testid="admin-push-tab">
      {/* Composer */}
      <div className="card-surface rounded-2xl p-5 space-y-4">
        <div className="flex items-center gap-2">
          <BellRing className="w-5 h-5" style={{ color: "var(--brand)" }} />
          <div className="display font-bold text-lg">Nuova notifica push</div>
        </div>

        <div>
          <Label className="text-xs uppercase tracking-widest text-slate-500">Destinatari</Label>
          <div className="mt-2 grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => setScope("broadcast")}
              data-testid="push-scope-broadcast"
              className={`rounded-xl border px-4 py-3 text-left transition-colors ${
                scope === "broadcast"
                  ? "border-[var(--brand)] bg-[var(--brand)]/5"
                  : "border-slate-200 hover:border-slate-300"
              }`}
            >
              <div className="flex items-center gap-2">
                <Users2 className="w-4 h-4" style={{ color: "var(--brand)" }} />
                <span className="font-black text-sm">Broadcast</span>
              </div>
              <div className="text-xs text-slate-500 mt-1">
                Tutti gli iscritti push ({subs.total} fantall. · {totalDevices} device)
              </div>
            </button>
            <button
              type="button"
              onClick={() => setScope("user")}
              data-testid="push-scope-user"
              className={`rounded-xl border px-4 py-3 text-left transition-colors ${
                scope === "user"
                  ? "border-[var(--brand)] bg-[var(--brand)]/5"
                  : "border-slate-200 hover:border-slate-300"
              }`}
            >
              <div className="flex items-center gap-2">
                <Send className="w-4 h-4" style={{ color: "var(--brand)" }} />
                <span className="font-black text-sm">Singolo fantallenatore</span>
              </div>
              <div className="text-xs text-slate-500 mt-1">Seleziona un destinatario</div>
            </button>
          </div>
        </div>

        {scope === "user" && (
          <div>
            <Label className="text-xs uppercase tracking-widest text-slate-500">Fantallenatore</Label>
            <Select value={userId} onValueChange={setUserId}>
              <SelectTrigger className="mt-2 bg-slate-50" data-testid="push-user-select">
                <SelectValue placeholder={subs.total === 0 ? "Nessun iscritto push" : "Scegli destinatario…"} />
              </SelectTrigger>
              <SelectContent>
                {subs.items.map((s) => (
                  <SelectItem key={s.user_id} value={s.user_id} data-testid={`push-user-opt-${s.user_id}`}>
                    {(s.team_name || s.full_name || s.user_id)} · Girone {s.girone || "—"} · {s.subs_count} device
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        <div>
          <Label className="text-xs uppercase tracking-widest text-slate-500">Titolo</Label>
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value.slice(0, 60))}
            placeholder="Es. Giornata 12 chiusa"
            className="mt-2 bg-slate-50"
            data-testid="push-title-input"
          />
          <div className="text-[10px] text-slate-400 mt-1 text-right">{title.length}/60</div>
        </div>

        <div>
          <Label className="text-xs uppercase tracking-widest text-slate-500">Testo</Label>
          <Textarea
            value={body}
            onChange={(e) => setBody(e.target.value.slice(0, 160))}
            placeholder="MVP: PIRATA CULO ROTTO · 87.5 pt — 1° ESPERTO · 2° SMOKING BIANCO · 3° KANTE CABRIOLET"
            rows={3}
            className="mt-2 bg-slate-50"
            data-testid="push-body-input"
          />
          <div className="text-[10px] text-slate-400 mt-1 text-right">{body.length}/160</div>
        </div>

        <div>
          <Label className="text-xs uppercase tracking-widest text-slate-500">URL di apertura</Label>
          <Input
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="/dashboard oppure /campionato/classifica-generale"
            className="mt-2 bg-slate-50"
            data-testid="push-url-input"
          />
          <div className="text-[10px] text-slate-400 mt-1">
            La push aprirà questo path quando l'utente tocca la notifica.
          </div>
        </div>

        {/* Live preview */}
        <div>
          <Label className="text-xs uppercase tracking-widest text-slate-500">Anteprima</Label>
          <div
            className="mt-2 rounded-2xl p-4 flex gap-3 items-start shadow-inner"
            style={{ background: "#0f172a", color: "#fff" }}
            data-testid="push-preview"
          >
            <div className="w-11 h-11 rounded-xl bg-white flex items-center justify-center shrink-0 ring-1 ring-black/10">
              <img src="/logo-fife-192.png" alt="FIFE" className="w-9 h-9 object-contain aspect-square" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="display font-black text-sm truncate">{title || "Titolo della push…"}</div>
              <div className="text-white/80 text-xs mt-0.5 line-clamp-3">
                {body || "Il testo che vedranno i tifosi nella notifica del sistema."}
              </div>
              <div className="text-[10px] text-white/50 mt-1">FIFE · {url || "/"}</div>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between pt-2 gap-3">
          <div className="text-xs text-slate-500">
            {scope === "broadcast"
              ? `Verrà spedita a ${totalDevices} device di ${subs.total} fantallenatori.`
              : userId
                ? `Destinatario: ${(subs.items.find((s) => s.user_id === userId)?.team_name) || userId}`
                : "Seleziona un destinatario"}
          </div>
          <Button
            onClick={send}
            disabled={busy || !title.trim() || !body.trim() || (scope === "user" && !userId)}
            className="btn-brand rounded-full h-11 px-6"
            data-testid="push-send-btn"
          >
            <Send className="w-4 h-4 mr-2" />
            {busy ? "Invio…" : "Invia notifica"}
          </Button>
        </div>

        {lastResult && (
          <div className="rounded-xl bg-slate-50 border border-slate-200 p-3 text-xs text-slate-700" data-testid="push-last-result">
            <span className="font-black uppercase tracking-widest text-slate-500 mr-2">Esito</span>
            {JSON.stringify(lastResult)}
          </div>
        )}
      </div>

      {/* Subscribers panel */}
      <div className="card-surface rounded-2xl p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Users2 className="w-5 h-5 text-slate-500" />
            <div className="display font-bold">Iscritti push</div>
          </div>
          <button onClick={loadSubs} className="text-xs text-slate-500 hover:text-slate-900" data-testid="push-refresh">
            ricarica
          </button>
        </div>
        {subs.total === 0 ? (
          <div className="text-sm text-slate-500 py-6 text-center" data-testid="push-subs-empty">
            Nessun fantallenatore ha ancora attivato le notifiche push.
            <div className="text-xs text-slate-400 mt-1">
              Chiedi ai tifosi di aprire la campanella in alto a destra e cliccare "Attiva".
            </div>
          </div>
        ) : (
          <ul className="space-y-2 max-h-[520px] overflow-y-auto pr-1" data-testid="push-subs-list">
            {subs.items.map((s) => (
              <li key={s.user_id}
                  className="rounded-xl border border-slate-200 px-3 py-2 flex items-center gap-2 hover:border-[var(--brand)]/50 cursor-pointer"
                  onClick={() => { setScope("user"); setUserId(s.user_id); }}
                  data-testid={`push-sub-${s.user_id}`}>
                <GironeBadge girone={s.girone} size={18} />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold truncate">{s.team_name || s.full_name || s.user_id}</div>
                  {s.full_name && s.team_name && (
                    <div className="text-[11px] text-slate-500 truncate">{s.full_name}</div>
                  )}
                </div>
                <span className="text-[10px] font-black rounded-full px-2 py-0.5 bg-slate-100 text-slate-700">
                  {s.subs_count} 📱
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

// ============ SUPPORTER TAB ============
function SupporterTab() {
  const [data, setData] = useState({ supporters: [], requests: [], totals: {} });
  const [filter, setFilter] = useState("all"); // all | internal | external

  const load = async () => {
    try {
      const { data } = await api.get("/admin/supporters");
      setData(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore caricamento supporter");
    }
  };
  useEffect(() => { load(); }, []);

  const filtered = (data.supporters || []).filter((s) => {
    if (filter === "internal") return !!s.from_user_id;
    if (filter === "external") return !s.from_user_id;
    return true;
  });

  return (
    <div className="space-y-4" data-testid="admin-supporter-tab">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        <StatBox label="Totale" value={data.totals?.supporters ?? 0} color="#3b56e0" />
        <StatBox label="Iscritti FIFE" value={data.totals?.internal ?? 0} color="#22c55e" />
        <StatBox label="Esterni (link)" value={data.totals?.external ?? 0} color="#f472b6" />
        <StatBox label="Richieste pending" value={data.totals?.requests_pending ?? 0} color="#f59e0b" />
        <StatBox label="Accettate" value={data.totals?.requests_accepted ?? 0} color="#0ea5e9" />
      </div>

      <div className="card-surface rounded-2xl p-5">
        <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
          <div className="display font-bold text-lg">Elenco supporter</div>
          <div className="flex gap-1">
            {["all", "internal", "external"].map((f) => (
              <button key={f} onClick={() => setFilter(f)}
                      data-testid={`admin-sup-filter-${f}`}
                      className={`rounded-full px-3 h-8 text-xs font-black uppercase tracking-widest ${
                        filter === f ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-700"
                      }`}>
                {f === "all" ? "Tutti" : f === "internal" ? "FIFE" : "Link"}
              </button>
            ))}
          </div>
        </div>

        {filtered.length === 0 ? (
          <div className="text-sm text-slate-500 py-6 text-center">Nessun supporter con questo filtro.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="admin-supporter-table">
              <thead>
                <tr className="text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-200">
                  <th className="text-left py-2 px-2">Supporter</th>
                  <th className="text-left py-2 px-2 hidden md:table-cell">Email / origine</th>
                  <th className="text-left py-2 px-2">Squadra beneficiata</th>
                  <th className="text-left py-2 px-2 hidden lg:table-cell">Data</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((s, i) => (
                  <tr key={s.id || i} className="border-b border-slate-100" data-testid={`admin-sup-row-${i}`}>
                    <td className="py-2 px-2">
                      <div className="font-semibold text-slate-900">{s.from_name || s.external_full_name || "—"}</div>
                      {s.from_team && <div className="text-xs text-slate-500">{s.from_team}</div>}
                    </td>
                    <td className="py-2 px-2 hidden md:table-cell text-slate-600">
                      {s.from_user_id ? (
                        <span className="text-[10px] font-black rounded-full px-2 py-0.5 bg-green-50 text-green-700">FIFE</span>
                      ) : (
                        <>
                          <span className="text-[10px] font-black rounded-full px-2 py-0.5 bg-pink-50 text-pink-700 mr-2">LINK</span>
                          <span className="text-xs">{s.external_email || "—"}</span>
                        </>
                      )}
                    </td>
                    <td className="py-2 px-2 text-slate-700">{s.target_team || "—"}</td>
                    <td className="py-2 px-2 hidden lg:table-cell text-xs text-slate-500">
                      {(s.created_at || "").slice(0, 10)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}




